import React, { CSSProperties, JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import classNames from 'classnames'
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  MinusIcon,
  PencilSquareIcon,
  PencilIcon,
  PlusIcon,
  RectangleGroupIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { PlayCircleIcon, StopCircleIcon, SignalIcon, SparklesIcon } from '@heroicons/react/24/solid'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { MerchTestModeControls, merchTestModeOf } from 'components/MerchTestModeControls'
import { YouTubeChatMissingWarning } from 'components/YouTubeChatMissingWarning'
import { NumberInput } from 'components/NumberInput'
import { ColorInput } from 'components/ColorPickers'
import { ScaledStage } from 'components/ScaledStage'
import { PresenceLayer, ScenePresence, useScenePresence } from 'components/ScenePresence'
import { POLL, TUG, SCOREBOARD } from 'lib/elementCanvas'
import { adoptTickerWeights, shiftTickerBoundary, tickerBlockFractions } from 'lib/tickerBlocks'
import { elementDisplayName, sortByName } from 'lib/elementOrder'
import {
  deriveDefaultBox,
  ELEMENT_BASE,
  ResolvedElementBox,
  resolveElementBox
} from 'lib/sceneElementBox'
import { PresenceItem, SceneTransition, clipGlideTransitionOf, glideTransitionOf, takeIdOf } from 'lib/scenePresence'
import {
  HistoryDirection,
  LayoutEntry,
  SceneHistories,
  WeightsEntry,
  WeightsSlot,
  WeightsSnapshot,
  clearDomainEverywhere,
  clearSceneDomain,
  layoutEntryApplies,
  normalizeWeightsSlot,
  pruneScenes,
  pruneWeights,
  recordSceneEdit,
  stepSceneHistory,
  topEntryOf,
  weightsEntryApplies
} from 'lib/sceneEditHistory'
import { historyShortcutOf, isMacPlatform, isTypingTarget, redoShortcutHint, undoShortcutHint } from 'lib/keyboardShortcuts'
import {
  clampRange,
  collectGuideCandidates,
  elementsEqual,
  GuideCandidates,
  MAX_ELEMENT_SIZE,
  MIN_VISUAL,
  Rect,
  SNAP_SCREEN_PX,
  snapEdge,
  snapPositions
} from 'lib/sceneEditorGeometry'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { useSocketToken } from 'context/socketToken'
import { useEventDetail } from 'hooks/useEventDetail'
import { useMerchSurfaceVisible } from 'hooks/useMerchSurfaceVisible'
import {
  useChannelEvents,
  useOverlayStructureEvents,
  OverlayStructureEnvelope
} from 'hooks/useOverlaySubscription'
import { useAccountOverlayLink } from 'hooks/useAccountOverlayLink'
import { useTriviaAdminFold } from 'hooks/useTriviaAdminFold'
import { useOverlaySocketCredentialState } from 'hooks/useOverlaySocketCredential'
import { useDefaultOverlayLink, buildOverlayShortUrl } from 'hooks/useOverlayLinks'
import { graphqlRequest } from 'lib/helpers'
import {
  COMPOSITE_OVERLAY_SEGMENT,
  CompositeOverlayKind,
  eventSectionPath,
  QUICK_OVERLAYS_PATH
} from 'lib/paths'
import {
  CameraControlSummary,
  ChatOverlaySummary,
  Competitor,
  DeletionResult,
  EmoteWallSummary,
  EventDetail,
  EventOverlay,
  GiveawaySummary,
  GiveawayWinner,
  MerchMoveDirection,
  MerchMoveResult,
  MerchOverlaySummary,
  OverlayScene,
  PollSummary,
  SceneElement,
  ScoreboardSummary,
  TickerBar,
  TriviaSummary,
  TugSummary
} from 'types/api'
import {
  PollPreview,
  draftFromPoll,
  pollLiveFromSummary
} from 'components/PollPreview'
import {
  TriviaPreview,
  draftFromTrivia,
  triviaLiveFromSummary
} from 'components/TriviaPreview'
import {
  triviaPhaseOf,
  triviaRevealArmed,
  triviaResetConfirmCopy,
  triviaYouTubeCounting,
  triviaYouTubeTimedOut,
  triviaRevealNowOffered,
  triviaCountingButtonLabel,
  triviaCountingTooltip,
  useTriviaGraceTick,
  TriviaStatusChip,
  TRIVIA_REVEAL_NOW_CONFIRM,
  TRIVIA_REVEAL_NOW_TOOLTIP,
  TRIVIA_STILL_COUNTING_LABEL,
  TRIVIA_STILL_COUNTING_TOOLTIP
} from 'lib/triviaPhase'
import { ConfirmDialog } from 'components/ConfirmDialog'
import { TugPreview, draftFromTug, tugLiveFromSummary } from './TugsPage'
import { ScoreboardPreview, draftFromScoreboard, scoreboardLiveFromSummary } from './ScoreboardPage'
import {
  GiveawayPreview,
  GiveawayWinnersTable,
  draftFromGiveaway,
  giveawayLiveFromSummary,
  promptDisqualifyReason,
  GIVEAWAY_STATUS_LABEL,
  GIVEAWAY_STATUS_PILL
} from './GiveawaysPage'
import getOverlayQuery from 'graphql/getOverlay.graphql'
import createOverlayMutation from 'graphql/createOverlay.graphql'
import createSceneMutation from 'graphql/createScene.graphql'
import renameSceneMutation from 'graphql/renameScene.graphql'
import deleteSceneMutation from 'graphql/deleteScene.graphql'
import setSceneElementsMutation from 'graphql/setSceneElements.graphql'
import takeSceneLiveMutation from 'graphql/takeSceneLive.graphql'
import clearOverlayLiveMutation from 'graphql/clearOverlayLive.graphql'
import republishEventStateMutation from 'graphql/republishEventState.graphql'
import startPollMutation from 'graphql/startPoll.graphql'
import stopPollMutation from 'graphql/stopPoll.graphql'
import resetPollCountsMutation from 'graphql/resetPollCounts.graphql'
import updatePollWeightsMutation from 'graphql/updatePollWeights.graphql'
import startTriviaMutation from 'graphql/startTrivia.graphql'
import stopTriviaMutation from 'graphql/stopTrivia.graphql'
import resetTriviaMutation from 'graphql/resetTrivia.graphql'
import closeTriviaMutation from 'graphql/closeTrivia.graphql'
import revealTriviaMutation from 'graphql/revealTrivia.graphql'
import startTugOfWarMutation from 'graphql/startTugOfWar.graphql'
import stopTugOfWarMutation from 'graphql/stopTugOfWar.graphql'
import resetTugOfWarCountsMutation from 'graphql/resetTugOfWarCounts.graphql'
import startCameraControlMutation from 'graphql/startCameraControl.graphql'
import stopCameraControlMutation from 'graphql/stopCameraControl.graphql'
import resetCameraControlCountsMutation from 'graphql/resetCameraControlCounts.graphql'
import startGiveawayMutation from 'graphql/startGiveaway.graphql'
import stopGiveawayMutation from 'graphql/stopGiveaway.graphql'
import calculateGiveawayWinnersMutation from 'graphql/calculateGiveawayWinners.graphql'
import publishGiveawayWinnersMutation from 'graphql/publishGiveawayWinners.graphql'
import resetGiveawayMutation from 'graphql/resetGiveaway.graphql'
import disqualifyGiveawayWinnerMutation from 'graphql/disqualifyGiveawayWinner.graphql'
import updateCompetitorsMutation from 'graphql/updateCompetitors.graphql'
import listMyMerchOverlaysQuery from 'graphql/listMyMerchOverlays.graphql'
import moveMerchBoardItemMutation from 'graphql/moveMerchBoardItem.graphql'
import { MerchBoardView, MerchEnvelope } from 'components/MerchBoardView'
import { ChatCanvasPreview, EditorPreviewHubProvider, EmoteWallCanvasPreview } from 'components/overlayPreview/EditorPreviewHub'
import { SourceMarkLinkPane } from 'components/chatOverlays/sourceMarkLinks'
import {
  CHAT_LINE_REMOVE_FAILED,
  CHAT_LINE_REMOVE_RETRY,
  ChatLineRemovePane,
  ChatLineRemoval,
  createChatLineRemover,
  removeChatLineVariablesOf
} from 'components/chatOverlays/chatLineRemove'
import { ChatGateCounts, ChatGateStatusLines, ChatGateStatusPane, EMPTY_CHAT_GATE_COUNTS } from 'components/chatOverlays/chatGateStatus'
import removeChatLineMutation from 'graphql/removeChatLine.graphql'
import { merchDisplayStyleOf } from 'lib/merchTickerConfig'
import { TickerBarMarquee, resolveTickerBarStyle, tickerBarHeightOf } from 'components/TickerBarMarquee'
import { tickerBarPreviewOfRow } from 'lib/tickerBarConfig'
import { ensureFontLoaded } from 'lib/fontLoader'
import { useTickerBarSurfaceVisible } from 'hooks/useTickerBarSurfaceVisible'

type OverlayCore = Omit<EventOverlay, 'scenes'>
type ElementKind = SceneElement['elementKind']

interface LiveTake {
  version: number | null
  takeId: string | null
  transition: SceneTransition
}
const NO_LIVE_TAKE: LiveTake = { version: null, takeId: null, transition: 'CUT' }
const foldLiveTake = (prev: LiveTake, core: OverlayCore, transition: SceneTransition): LiveTake => {
  if (prev.version != null && prev.version >= core.version) return prev
  const takeId = takeIdOf(core.liveScene)
  if (takeId === prev.takeId) return { ...prev, version: core.version }
  return { version: core.version, takeId, transition }
}
const NO_LIVE_ELEMENTS: SceneElement[] = []

const KIND_NOUN: Record<CompositeOverlayKind, string> = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile'
}

const FALLBACK_DEFAULT_ELEMENT_SCALE: Record<CompositeOverlayKind, number> = {
  DESKTOP: 1,
  MOBILE: 0.6
}

const ADD_Y_FRACTION = 400 / 1080

const MAX_SCENES_PER_OVERLAY = 30
const MAX_ELEMENTS_PER_SCENE = 25
const POS_MIN = -4000
const POS_MAX = 8000
const SCALE_MIN = 0.1
const SCALE_MAX = 4

const quantizeScale = (s: number, max: number): number => {
  if (Math.abs(s - 1) <= 0.005 && max >= 1) return 1
  const rounded = Math.round(s * 100) / 100
  return rounded > max ? Math.floor(s * 100) / 100 : rounded
}

const DRAG_THRESHOLD_PX = 3

const RESIZE_HANDLE_BASE_PX = 28
const resizeHandleSize = (scale: number): number =>
  Math.min(480, Math.max(12, RESIZE_HANDLE_BASE_PX / scale))

type ResizeHandle = 'tl' | 'tr' | 'bl' | 'br' | 'n' | 's' | 'e' | 'w'
const RESIZE_HANDLES: Array<{ handle: ResizeHandle, fx: 0 | 0.5 | 1, fy: 0 | 0.5 | 1, cursor: 'nwse-resize' | 'nesw-resize' | 'ns-resize' | 'ew-resize' }> = [
  { handle: 'tl', fx: 0, fy: 0, cursor: 'nwse-resize' },
  { handle: 'tr', fx: 1, fy: 0, cursor: 'nesw-resize' },
  { handle: 'bl', fx: 0, fy: 1, cursor: 'nesw-resize' },
  { handle: 'br', fx: 1, fy: 1, cursor: 'nwse-resize' },
  { handle: 'n', fx: 0.5, fy: 0, cursor: 'ns-resize' },
  { handle: 's', fx: 0.5, fy: 1, cursor: 'ns-resize' },
  { handle: 'w', fx: 0, fy: 0.5, cursor: 'ew-resize' },
  { handle: 'e', fx: 1, fy: 0.5, cursor: 'ew-resize' }
]
const handleMovesEast = (h: ResizeHandle): boolean => h === 'tr' || h === 'br' || h === 'e'
const handleMovesWest = (h: ResizeHandle): boolean => h === 'tl' || h === 'bl' || h === 'w'
const handleMovesSouth = (h: ResizeHandle): boolean => h === 'bl' || h === 'br' || h === 's'
const handleMovesNorth = (h: ResizeHandle): boolean => h === 'tl' || h === 'tr' || h === 'n'
const isCornerHandle = (h: ResizeHandle): boolean => h.length === 2

const GUIDE_COLOR = 'rgba(236,72,153,0.9)'

const KIND_LABELS: Record<ElementKind, string> = {
  POLL: 'Poll',
  TUG: 'Tug',
  BOARD: 'Board',
  CHAT: 'Chat',
  EMOTEWALL: 'Emote wall',
  MERCH: 'Merch board',
  MERCH_TICKER: 'Merch ticker',
  CAMERA_CONTROL: 'Camera control',
  GIVEAWAY: 'Giveaway',
  TRIVIA: 'Trivia',
  TICKER_BAR: 'Message ticker'
}

const isCenterAnchoredKind = (kind: ElementKind): boolean =>
  kind === 'POLL' || kind === 'GIVEAWAY' || kind === 'TRIVIA'

const EDIT_SECTIONS: Record<Exclude<ElementKind, 'MERCH' | 'MERCH_TICKER'>, string> = {
  POLL: 'polls',
  TUG: 'tug-of-wars',
  BOARD: 'scoreboard',
  CHAT: 'chats',
  EMOTEWALL: 'emote-walls',
  CAMERA_CONTROL: 'camera-controls',
  GIVEAWAY: 'giveaways',
  TRIVIA: 'trivia',
  TICKER_BAR: 'message-tickers'
}

const elementEditPath = (eventId: string, el: SceneElement): string => {
  if (el.elementKind === 'MERCH' || el.elementKind === 'MERCH_TICKER') {
    return `${QUICK_OVERLAYS_PATH}/merch/${el.elementId}`
  }
  return eventSectionPath(eventId, EDIT_SECTIONS[el.elementKind])
}

const elementKeyOf = (el: Pick<SceneElement, 'elementKind' | 'elementId'>): string =>
  `${el.elementKind}#${el.elementId}`

const clampPos = (n: number): number =>
  Math.min(POS_MAX, Math.max(POS_MIN, Math.round(n)))

const paneCanvasMapping = (
  rect: { width: number, height: number },
  canvasW: number,
  canvasH: number
): { scale: number, offsetX: number, offsetY: number } => {
  const scale = Math.min(rect.width / canvasW, rect.height / canvasH)
  return {
    scale,
    offsetX: (rect.width - canvasW * scale) / 2,
    offsetY: (rect.height - canvasH * scale) / 2
  }
}

interface ResizeGesture {
  key: string
  sceneId: string
  pointerId: number
  handle: ResizeHandle
  kind: ElementKind
  left: number
  top: number
  right: number
  bottom: number
  layoutWidth: number
  contentH: number
  innerW: number
  innerH: number
  candidates: GuideCandidates
  curX: number
  curY: number
  curW: number
  curH: number
  curScale: number
  changed: boolean
  scaled: boolean
}

const uniformScaleFrame = (r: ResizeGesture): { refW: number, refH: number, maxScale: number } => {
  const autoGrow = isCenterAnchoredKind(r.kind) || r.kind === 'MERCH' || r.kind === 'MERCH_TICKER' || r.kind === 'TICKER_BAR'
  const refW = autoGrow ? r.layoutWidth : r.innerW
  const refH = autoGrow ? r.contentH : r.innerH
  return { refW, refH, maxScale: Math.min(SCALE_MAX, MAX_ELEMENT_SIZE / refW, MAX_ELEMENT_SIZE / refH) }
}

const applyUniformScale = (r: ResizeGesture, s: number): void => {
  const { refW, refH } = uniformScaleFrame(r)
  r.curScale = s
  r.curW = Math.round(refW * s)
  r.curH = Math.round(refH * s)
  r.curX = clampPos(handleMovesEast(r.handle) ? r.left : r.right - refW * s)
  r.curY = isCenterAnchoredKind(r.kind)
    ? clampPos(handleMovesSouth(r.handle) ? r.top + (refH * s) / 2 : r.bottom - (refH * s) / 2)
    : clampPos(handleMovesSouth(r.handle) ? r.top : r.bottom - refH * s)
}

const sceneOrderStorageKey = (eventId: string, overlayId: string): string =>
  `stream-tools.scene-order.${eventId}.${overlayId}`

const readStoredSceneOrder = (eventId: string, overlayId: string): string[] => {
  try {
    const raw = globalThis.localStorage.getItem(sceneOrderStorageKey(eventId, overlayId))
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

const writeStoredSceneOrder = (eventId: string, overlayId: string, order: string[]): void => {
  try {
    globalThis.localStorage.setItem(sceneOrderStorageKey(eventId, overlayId), JSON.stringify(order))
  } catch {
  }
}

const reconcileSceneOrder = (local: string[], membership: string[]): string[] => {
  const known = new Set(membership)
  const kept = local.filter((id) => known.has(id))
  const seen = new Set(kept)
  return [...kept, ...membership.filter((id) => !seen.has(id))]
}

const toElementInput = (el: SceneElement): SceneElement => ({
  elementKind: el.elementKind,
  elementId: el.elementId,
  x: el.x,
  y: el.y,
  z: el.z,
  scale: el.scale,
  visible: el.visible,
  w: el.w,
  h: el.h
})

const questionElementLabel = (row: { name: string, question: string } | undefined): string | null =>
  row == null ? null : elementDisplayName(row.name, row.question)

export const resolveElementName = (
  event: EventDetail,
  streamChats: ChatOverlaySummary[],
  streamWalls: EmoteWallSummary[],
  accountBoards: MerchOverlaySummary[],
  el: SceneElement
): string | null => {
  if (el.elementKind === 'POLL') return questionElementLabel(event.polls.find((p) => p.pollId === el.elementId))
  if (el.elementKind === 'TUG') return questionElementLabel(event.tugs.find((t) => t.tugId === el.elementId))
  if (el.elementKind === 'BOARD') return event.scoreboards.find((s) => s.boardId === el.elementId)?.name ?? null
  if (el.elementKind === 'CAMERA_CONTROL') return event.cameraControls.find((c) => c.camControlId === el.elementId)?.name ?? null
  if (el.elementKind === 'GIVEAWAY') return event.giveaways.find((g) => g.giveawayId === el.elementId)?.name ?? null
  if (el.elementKind === 'TRIVIA') return questionElementLabel(event.trivias.find((t) => t.triviaId === el.elementId))
  if (el.elementKind === 'EMOTEWALL') return streamWalls.find((w) => w.emoteWallId === el.elementId)?.name ?? null
  if (el.elementKind === 'MERCH' || el.elementKind === 'MERCH_TICKER') {
    return accountBoards.find((b) => b.merchOverlayId === el.elementId)?.name ?? null
  }
  if (el.elementKind === 'TICKER_BAR') return streamTickerBarsOf(event).find((t) => t.tickerBarId === el.elementId)?.name ?? null
  return streamChats.find((c) => c.chatId === el.elementId)?.name ?? null
}

const withAddedRows = <T,>(prev: T[], fresh: readonly T[], idOf: (row: T) => string): T[] => {
  const known = new Set(prev.map(idOf))
  const added = fresh.filter((row) => !known.has(idOf(row)))
  return added.length === 0 ? prev : [...prev, ...added]
}

const NO_STREAM_CHATS: ChatOverlaySummary[] = []
const NO_STREAM_WALLS: EmoteWallSummary[] = []
export const streamChatsOf = (event: Pick<EventDetail, 'chatOverlays'>): ChatOverlaySummary[] =>
  event.chatOverlays ?? NO_STREAM_CHATS
export const streamWallsOf = (event: Pick<EventDetail, 'emoteWalls'>): EmoteWallSummary[] =>
  event.emoteWalls ?? NO_STREAM_WALLS

const NO_STREAM_TICKER_BARS: TickerBar[] = []
export const streamTickerBarsOf = (event: Pick<EventDetail, 'tickerBars'>): TickerBar[] =>
  event.tickerBars ?? NO_STREAM_TICKER_BARS

export const tickerBarUnscaledHeightOf = (rows: readonly TickerBar[], tickerBarId: string): number => {
  const row = rows.find((t) => t.tickerBarId === tickerBarId)
  return row == null ? ELEMENT_BASE.TICKER_BAR.h : tickerBarHeightOf(resolveTickerBarStyle(row.style))
}

export const streamElementCandidatesOf = (event: Pick<EventDetail, 'chatOverlays' | 'emoteWalls'>): Array<{ kind: 'CHAT' | 'EMOTEWALL', id: string, name: string }> => [
  ...streamChatsOf(event).map((c) => ({ kind: 'CHAT' as const, id: c.chatId, name: c.name })),
  ...streamWallsOf(event).map((w) => ({ kind: 'EMOTEWALL' as const, id: w.emoteWallId, name: w.name }))
]

type VotingStatus = 'voting' | 'stopped' | 'not-started'

const votingStatusOf = (s: { status: 'ACTIVE' | 'INACTIVE', stoppedAt: string | null }): VotingStatus =>
  s.status === 'ACTIVE' ? 'voting' : (s.stoppedAt !== null ? 'stopped' : 'not-started')

const VOTING_CHIP_STYLES: Record<VotingStatus, { label: string, text: string, dot: string }> = {
  voting: { label: 'Voting', text: 'text-emerald-300', dot: 'bg-emerald-400' },
  stopped: { label: 'Stopped', text: 'text-amber-300', dot: 'bg-amber-400' },
  'not-started': { label: 'Not started', text: 'text-zinc-400', dot: 'bg-zinc-400' }
}

const VotingStatusChip = ({ status }: { status: VotingStatus }): JSX.Element => {
  const style = VOTING_CHIP_STYLES[status]
  return (
    <span className={classNames('inline-flex items-center gap-1.5 text-[11px] font-medium', style.text)}>
      <span className={classNames('h-1.5 w-1.5 shrink-0 rounded-full', style.dot)} />
      {style.label}
    </span>
  )
}

const pollResetConfirmCopy = (name: string, live: boolean): string =>
  live
    ? `Reset "${name}"?\n\n` +
      'WARNING: this stops the poll now, permanently DELETES every recorded vote (Twitch and YouTube) and sets all counts back to zero. It cannot be undone.\n\n' +
      'The poll goes back to not started. Press Start to run it again.\n\n' +
      'To KEEP a round\'s votes, create a NEW poll instead of resetting.\n\n' +
      'Stop, delete all votes and reset?'
    : `Reset "${name}"?\n\n` +
      'WARNING: this permanently DELETES every recorded vote (Twitch and YouTube) for this poll and sets all counts back to zero. It cannot be undone.\n\n' +
      'The poll goes back to not started. Press Start to run it again.\n\n' +
      'To KEEP a round\'s votes, create a NEW poll instead of resetting.\n\n' +
      'Delete all votes and reset?'

const pollResetIsLive = (poll: Pick<PollSummary, 'status' | 'graceEndsAt'>, nowMs: number): boolean =>
  poll.status === 'ACTIVE' || (poll.graceEndsAt != null && Date.parse(poll.graceEndsAt) > nowMs)

type PollResetResponse = Pick<PollSummary, 'pollId' | 'status' | 'startedAt' | 'stoppedAt' | 'graceEndsAt' | 'version' | 'totalVotes' | 'options'>

const foldPollReset = (p: PollSummary, updated: PollResetResponse): PollSummary => {
  if (p.version > updated.version) return p
  const countsById = new Map(updated.options.map((o) => [o.optionId, o]))
  return {
    ...p,
    status: updated.status,
    startedAt: updated.startedAt,
    stoppedAt: updated.stoppedAt,
    graceEndsAt: updated.graceEndsAt ?? null,
    version: updated.version,
    totalVotes: updated.totalVotes,
    options: p.options.map((o) => {
      const reset = countsById.get(o.optionId)
      return {
        ...o,
        voteCount: reset?.voteCount ?? 0,
        voteCountByPlatform: reset?.voteCountByPlatform ?? { twitch: 0, youtube: 0 }
      }
    })
  }
}

const tugResetConfirmCopy = (name: string, live: boolean): string =>
  live
    ? `Reset "${name}"?\n\n` +
      'WARNING: this stops the tug of war now, permanently DELETES every recorded vote (Twitch and YouTube) and sets both sides back to zero. It cannot be undone.\n\n' +
      'The tug of war goes back to not started. Press Start to run it again.\n\n' +
      'To KEEP a round\'s votes, create a NEW tug of war instead of resetting.\n\n' +
      'Stop, delete all votes and reset?'
    : `Reset "${name}"?\n\n` +
      'WARNING: this permanently DELETES every recorded vote (Twitch and YouTube) for this tug of war and sets both sides back to zero. It cannot be undone.\n\n' +
      'The tug of war goes back to not started. Press Start to run it again.\n\n' +
      'To KEEP a round\'s votes, create a NEW tug of war instead of resetting.\n\n' +
      'Delete all votes and reset?'

const tugResetIsLive = (tug: Pick<TugSummary, 'status' | 'graceEndsAt'>, nowMs: number): boolean =>
  tug.status === 'ACTIVE' || (tug.graceEndsAt != null && Date.parse(tug.graceEndsAt) > nowMs)

type TugSideCounts = Pick<TugSummary['optionA'], 'voteCount' | 'voteCountByPlatform'>
type TugResetResponse = Pick<TugSummary, 'tugId' | 'status' | 'startedAt' | 'stoppedAt' | 'graceEndsAt' | 'version' | 'totalVotes'> & {
  optionA: TugSideCounts
  optionB: TugSideCounts
}

const foldTugReset = (t: TugSummary, updated: TugResetResponse): TugSummary => {
  if (t.version > updated.version) return t
  return {
    ...t,
    status: updated.status,
    startedAt: updated.startedAt,
    stoppedAt: updated.stoppedAt,
    graceEndsAt: updated.graceEndsAt ?? null,
    version: updated.version,
    totalVotes: updated.totalVotes,
    optionA: { ...t.optionA, voteCount: updated.optionA.voteCount, voteCountByPlatform: updated.optionA.voteCountByPlatform },
    optionB: { ...t.optionB, voteCount: updated.optionB.voteCount, voteCountByPlatform: updated.optionB.voteCountByPlatform }
  }
}

const PanePill = ({ variant }: { variant: 'preview' | 'live' }): JSX.Element => {
  if (variant === 'live') {
    return (
      <span className='inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400 ring-1 ring-inset ring-emerald-500/30'>
        <span className='h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-emerald-400' />
        Live
      </span>
    )
  }
  return (
    <span className='inline-flex items-center gap-1.5 rounded-full bg-zinc-500/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-300 ring-1 ring-inset ring-zinc-500/30'>
      <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-400' />
      Inspecting only
    </span>
  )
}

const CopyButton = ({ value, label }: { value: string, label: string }): JSX.Element => {
  const [copied, setCopied] = useState(false)
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      globalThis.setTimeout(() => setCopied(false), 1500)
    } catch {
    }
  }
  return (
    <button
      type='button'
      onClick={() => { void handleCopy() }}
      title={`Copy ${label}`}
      className='inline-flex items-center text-dm-text-accent hover:text-dm-text-secondary'
    >
      {copied
        ? <ClipboardDocumentCheckIcon className='size-4 text-emerald-400' aria-hidden='true' />
        : <ClipboardDocumentIcon className='size-4' aria-hidden='true' />}
      <span className='sr-only'>Copy {label}</span>
    </button>
  )
}

export const SceneElementContent = ({ element, event, streamChats, streamWalls, accountBoards, layoutWidth, layoutHeight, pollWeightsPreview = null, onMerchMeasure, onMerchEnvelope, onTickerBarReady, clipGlideMs = 0 }: {
  element: SceneElement
  event: EventDetail
  streamChats: ChatOverlaySummary[]
  streamWalls: EmoteWallSummary[]
  accountBoards: MerchOverlaySummary[]
  layoutWidth: number
  layoutHeight: number
  pollWeightsPreview?: { pollId: string, weights: number[] | null } | null
  onMerchMeasure?: (height: number) => void
  onMerchEnvelope?: (envelope: MerchEnvelope) => void
  onTickerBarReady?: () => void
  clipGlideMs?: number
}): JSX.Element | null => {
  if (element.elementKind === 'POLL') {
    const poll = event.polls.find((p) => p.pollId === element.elementId)
    if (poll === undefined) return null
    const draft = pollWeightsPreview !== null && pollWeightsPreview.pollId === poll.pollId
      ? { ...draftFromPoll(poll), tickerBlockWeights: pollWeightsPreview.weights }
      : draftFromPoll(poll)
    return <PollPreview draft={draft} live={pollLiveFromSummary(poll)} layoutWidth={layoutWidth} />
  }
  if (element.elementKind === 'TRIVIA') {
    const trivia = event.trivias.find((t) => t.triviaId === element.elementId)
    if (trivia === undefined) return null
    return <TriviaPreview draft={draftFromTrivia(trivia)} live={triviaLiveFromSummary(trivia)} layoutWidth={layoutWidth} />
  }
  if (element.elementKind === 'TUG') {
    const tug = event.tugs.find((t) => t.tugId === element.elementId)
    if (tug === undefined) return null
    return <TugPreview draft={draftFromTug(tug)} live={tugLiveFromSummary(tug)} layoutWidth={layoutWidth} />
  }
  if (element.elementKind === 'BOARD') {
    const board = event.scoreboards.find((s) => s.boardId === element.elementId)
    if (board === undefined) return null
    return <ScoreboardPreview draft={draftFromScoreboard(board)} live={scoreboardLiveFromSummary(board)} layoutWidth={layoutWidth} />
  }
  if (element.elementKind === 'GIVEAWAY') {
    const giveaway = event.giveaways.find((g) => g.giveawayId === element.elementId)
    if (giveaway === undefined) return null
    return <GiveawayPreview draft={draftFromGiveaway(giveaway)} state={giveaway.status} live={giveawayLiveFromSummary(giveaway)} layoutWidth={layoutWidth} />
  }
  if (element.elementKind === 'EMOTEWALL') {
    const wall = streamWalls.find((w) => w.emoteWallId === element.elementId)
    if (wall === undefined) return null
    return <EmoteWallCanvasPreview wall={wall} slug={event.slug} layoutWidth={layoutWidth} layoutHeight={layoutHeight} clipGlideMs={clipGlideMs} />
  }
  if (element.elementKind === 'MERCH') {
    const board = accountBoards.find((b) => b.merchOverlayId === element.elementId)
    if (board === undefined) return null
    return (
      <MerchCanvasPreview
        board={board}
        layoutWidth={layoutWidth}
        layoutHeight={layoutHeight}
        onMeasure={onMerchMeasure}
        onEnvelope={onMerchEnvelope}
      />
    )
  }
  if (element.elementKind === 'MERCH_TICKER') {
    const board = accountBoards.find((b) => b.merchOverlayId === element.elementId)
    if (board === undefined) return null
    return (
      <MerchCanvasPreview
        board={board}
        layoutWidth={layoutWidth}
        layoutHeight={layoutHeight}
        compactFallback
        onEnvelope={onMerchEnvelope}
      />
    )
  }
  if (element.elementKind === 'TICKER_BAR') {
    const row = streamTickerBarsOf(event).find((t) => t.tickerBarId === element.elementId)
    if (row == null) return null
    return <TickerBarCanvasPreview row={row} layoutWidth={layoutWidth} onReady={onTickerBarReady} />
  }
  const chat = streamChats.find((c) => c.chatId === element.elementId)
  if (chat === undefined) return null
  return <ChatCanvasPreview chat={chat} slug={event.slug} layoutWidth={layoutWidth} layoutHeight={layoutHeight} clipGlideMs={clipGlideMs} />
}

const TickerBarCanvasPreview = ({ row, layoutWidth, onReady }: {
  row: TickerBar
  layoutWidth: number
  onReady?: () => void
}): JSX.Element => {
  const envelope = useMemo(() => tickerBarPreviewOfRow(row), [row])
  const fontSig = useMemo(() => {
    const s = envelope.style
    const bars = s.showTopBar ? [s.top, s.bottom] : [s.bottom]
    return [...new Set(bars.flatMap((b) => [b.labelFontKey, b.textFontKey]))].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)).join('|')
  }, [envelope])
  useEffect(() => {
    void Promise.all(fontSig.split('|').filter((k) => k !== '').map(async (key) => await ensureFontLoaded(key))).catch(() => {

    })
  }, [fontSig])
  const readyRef = useRef(onReady)
  readyRef.current = onReady
  useEffect(() => {
    readyRef.current?.()
  }, [])
  return <TickerBarMarquee envelope={envelope} wrapWidth={layoutWidth} />
}

const MerchCanvasPreview = ({
  board,
  layoutWidth,
  layoutHeight,
  compactFallback = false,
  onMeasure,
  onEnvelope
}: {
  board: MerchOverlaySummary
  layoutWidth: number
  layoutHeight: number
  compactFallback?: boolean
  onMeasure?: (height: number) => void
  onEnvelope?: (envelope: MerchEnvelope) => void
}): JSX.Element => {
  const previewLink = useAccountOverlayLink('merch', board.merchOverlayId)
  const previewK = previewLink.link === null ? null : previewLink.link.k
  const { token: previewToken } = useOverlaySocketCredentialState({ k: previewK })
  const credentialError = previewLink.error
  const [live, setLive] = useState(false)
  const handleEnvelope = useCallback((envelope: MerchEnvelope): void => {
    setLive(true)
    onEnvelope?.(envelope)
  }, [onEnvelope])
  useEffect(() => {
    if (previewToken === null) setLive(false)
  }, [previewToken])

  return (
    <div
      ref={(node) => {
        if (node === null || onMeasure === undefined) return
        onMeasure(node.offsetHeight)
        const ro = new ResizeObserver(() => onMeasure(node.offsetHeight))
        ro.observe(node)
        return () => ro.disconnect()
      }}
    >
      {(!live || previewToken === null) && (
        <div
          style={{ width: layoutWidth, height: layoutHeight }}
          className={compactFallback
            ? 'flex flex-row items-center justify-center gap-4 rounded-lg border-2 border-dashed border-white/25 bg-white/5 px-6 text-center'
            : 'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-white/25 bg-white/5 px-6 text-center'}
        >
          <span style={{ fontSize: compactFallback ? 26 : 34 }} className='font-semibold text-white/80'>{board.name}</span>
          <span style={{ fontSize: compactFallback ? 20 : 26 }} className='text-white/50'>
            {credentialError !== null
              ? 'Preview link failed. The board still renders on the live overlay.'
              : 'Connecting live preview…'}
          </span>
        </div>
      )}
      {previewToken !== null && (
        <div style={{ display: live ? undefined : 'none' }}>
          <MerchBoardView
            merchOverlayId={board.merchOverlayId}
            token={previewToken}
            natural
            wrapWidth={layoutWidth}
            onEnvelope={handleEnvelope}
          />
        </div>
      )}
    </div>
  )
}

const MerchElementPanel = ({
  board,
  auth0Token,
  liveEnvelope,
  onBoardChanged
}: {
  board: MerchOverlaySummary
  auth0Token: string | undefined
  liveEnvelope: MerchEnvelope | null
  onBoardChanged: (overlay: MerchOverlaySummary) => void
}): JSX.Element => {
  const [liveRows, setLiveRows] = useState<Array<{ productId: string, title: string }> | null>(null)
  const [moveBusyId, setMoveBusyId] = useState<string | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  const priceBoard = board.mode !== 'ORDERS'

  useEffect(() => {
    if (liveEnvelope === null) return
    setLiveRows(liveEnvelope.products.map((p) => ({ productId: p.productId, title: p.title })))
  }, [liveEnvelope])

  const rows = liveRows ?? board.items.map((i) => ({ productId: i.productId, title: i.title }))

  const handleMove = async (productId: string, direction: MerchMoveDirection): Promise<void> => {
    if (auth0Token === undefined || moveBusyId !== null) return
    setMoveBusyId(productId)
    setMoveError(null)
    try {
      const { result } = await graphqlRequest<{ result: MerchMoveResult }>(
        moveMerchBoardItemMutation,
        { merchOverlayId: board.merchOverlayId, productId, direction },
        auth0Token
      )
      const titles = new Map(rows.map((r) => [r.productId, r.title]))
      setLiveRows(result.order.map((id) => ({ productId: id, title: titles.get(id) ?? id })))
    } catch (err) {
      setMoveError(err instanceof Error ? err.message : 'Failed to move the item')
    } finally {
      setMoveBusyId(null)
    }
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='text-sm font-medium text-dm-text-primary'>{board.name}</div>
      {moveError !== null && (
        <ErrorInline onDismiss={() => setMoveError(null)}>{moveError}</ErrorInline>
      )}
      {merchTestModeOf(board) && (
        <div data-testid='merch-panel-test-mode'>
          <div className='mb-1.5 text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Test mode
          </div>
          <MerchTestModeControls
            board={board}
            auth0Token={auth0Token}
            onBoardChanged={onBoardChanged}
          />
        </div>
      )}
      <div>
        <div className='mb-1.5 text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
          Board order
        </div>
        {rows.length === 0 && (
          <p className='rounded-md border border-dashed border-white/10 px-3 py-4 text-center text-xs text-dm-text-secondary'>
            No items on this board yet.
          </p>
        )}
        {rows.length > 0 && (
          <ul className='space-y-1' data-testid='merch-panel-order-list'>
            {rows.map((row, index) => (
              <li
                key={row.productId}
                className='flex items-center gap-2 rounded-md border border-white/5 bg-dm-bg-accent/30 px-2 py-1'
              >
                <span className='w-5 shrink-0 text-right text-[11px] tabular-nums text-dm-text-accent'>
                  {index + 1}
                </span>
                <span className='min-w-0 flex-1 truncate text-xs text-dm-text-primary'>{row.title}</span>
                {!priceBoard && (
                  <>
                    <button
                      type='button'
                      onClick={() => { void handleMove(row.productId, 'UP') }}
                      disabled={index === 0 || moveBusyId !== null}
                      title='Move up one ladder position (publishes live)'
                      className='rounded-md p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-40'
                    >
                      {moveBusyId === row.productId ? <LoadingSpinner /> : <ChevronUpIcon className='size-3.5' aria-hidden='true' />}
                      <span className='sr-only'>Move {row.title} up</span>
                    </button>
                    <button
                      type='button'
                      onClick={() => { void handleMove(row.productId, 'DOWN') }}
                      disabled={index === rows.length - 1 || moveBusyId !== null}
                      title='Move down one ladder position (publishes live)'
                      className='rounded-md p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-40'
                    >
                      <ChevronDownIcon className='size-3.5' aria-hidden='true' />
                      <span className='sr-only'>Move {row.title} down</span>
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className='mt-1.5 text-[11px] text-dm-text-accent'>
          {priceBoard
            ? 'Price-based board: rows sort by market movement (% change vs base price), so there is no manual reorder; this list mirrors the live order.'
            : 'One position per press: the move publishes to the live board immediately and flashes the row like a sale.'}
        </p>
      </div>
    </div>
  )
}

interface ElementOverride { key: string, x: number, y: number, scale: number, w: number, h: number }

interface ActiveGuides { vertical: number | null, horizontal: number | null, thickness: number }

const layoutHeightOf = (el: SceneElement, box: ResolvedElementBox, tickerBars: readonly TickerBar[] = NO_STREAM_TICKER_BARS): number => {
  if (isCenterAnchoredKind(el.elementKind)) return 0
  if (el.elementKind === 'MERCH') return ELEMENT_BASE.MERCH.h
  if (el.elementKind === 'MERCH_TICKER') return ELEMENT_BASE.MERCH_TICKER.h
  if (el.elementKind === 'TICKER_BAR') return tickerBarUnscaledHeightOf(tickerBars, el.elementId)
  if (el.elementKind === 'TUG') return TUG.height
  if (el.elementKind === 'BOARD') return SCOREBOARD.height
  if (el.w === null || el.h === null) return ELEMENT_BASE[el.elementKind].h
  return Math.max(1, Math.round(box.h / box.glyphScale))
}

const SceneCanvas = ({
  scene,
  event,
  streamChats,
  streamWalls,
  accountBoards,
  canvasWidth,
  canvasHeight,
  interactive,
  override,
  guides,
  selectedElementKey,
  onElementPointerDown,
  onElementPointerMove,
  onElementPointerUp,
  onElementPointerCancel,
  onResizePointerDown,
  onResizePointerMove,
  onResizePointerUp,
  onResizePointerCancel,
  pollWeightsPreview = null,
  onBoundaryPointerDown,
  onBoundaryPointerMove,
  onBoundaryPointerUp,
  onBoundaryPointerCancel,
  measuredHeights,
  onMeasure,
  onMerchEnvelope,
  presence
}: {
  scene: Pick<OverlayScene, 'elements'> | null
  event: EventDetail
  streamChats: ChatOverlaySummary[]
  streamWalls: EmoteWallSummary[]
  accountBoards: MerchOverlaySummary[]
  canvasWidth: number
  canvasHeight: number
  interactive: boolean
  override: ElementOverride | null
  guides: ActiveGuides | null
  selectedElementKey: string | null
  onElementPointerDown?: (e: React.PointerEvent<HTMLDivElement>, el: SceneElement) => void
  onElementPointerMove?: (e: React.PointerEvent<HTMLDivElement>) => void
  onElementPointerUp?: (e: React.PointerEvent<HTMLDivElement>) => void
  onElementPointerCancel?: (e: React.PointerEvent<HTMLDivElement>) => void
  onResizePointerDown?: (e: React.PointerEvent<HTMLDivElement>, el: SceneElement, handle: ResizeHandle) => void
  onResizePointerMove?: (e: React.PointerEvent<HTMLDivElement>) => void
  onResizePointerUp?: (e: React.PointerEvent<HTMLDivElement>) => void
  onResizePointerCancel?: (e: React.PointerEvent<HTMLDivElement>) => void
  pollWeightsPreview?: { pollId: string, weights: number[] | null } | null
  onBoundaryPointerDown?: (e: React.PointerEvent<HTMLDivElement>, el: SceneElement, boundary: number) => void
  onBoundaryPointerMove?: (e: React.PointerEvent<HTMLDivElement>) => void
  onBoundaryPointerUp?: (e: React.PointerEvent<HTMLDivElement>) => void
  onBoundaryPointerCancel?: (e: React.PointerEvent<HTMLDivElement>) => void
  measuredHeights?: Record<string, number>
  onMeasure?: (key: string, height: number) => void
  onMerchEnvelope?: (merchOverlayId: string, envelope: MerchEnvelope) => void
  presence?: ScenePresence
}): JSX.Element => {
  if (scene == null && presence == null) {
    return (
      <ScaledStage mode='pane' canvasWidth={canvasWidth} canvasHeight={canvasHeight}>
        {null}
      </ScaledStage>
    )
  }
  const rows: Array<{ el: SceneElement, item: PresenceItem | null }> = presence != null
    ? presence.items.map((item) => ({ el: item.el, item }))
    : [...(scene?.elements ?? [])]
        .filter((el) => el.visible && el.elementKind !== 'CAMERA_CONTROL')
        .sort((a, b) => a.z - b.z)
        .map((el) => ({ el, item: null }))
  return (
    <ScaledStage mode='pane' canvasWidth={canvasWidth} canvasHeight={canvasHeight}>
      {rows.map(({ el, item }) => {
        if (resolveElementName(event, streamChats, streamWalls, accountBoards, el) == null) return null
        const key = elementKeyOf(el)
        const active = override !== null && override.key === key ? override : null
        const shown: SceneElement = active !== null
          ? { ...el, x: active.x, y: active.y, scale: active.scale, w: active.w, h: active.h }
          : el
        const box = resolveElementBox(shown)
        const isPoll = isCenterAnchoredKind(el.elementKind)
        const isMerch = el.elementKind === 'MERCH'
        const isTicker = el.elementKind === 'MERCH_TICKER'
        const isTickerBar = el.elementKind === 'TICKER_BAR'
        const isClipKind = el.elementKind === 'CHAT' || el.elementKind === 'EMOTEWALL'
        const layoutHeight = isMerch
          ? measuredHeights?.[key] ?? ELEMENT_BASE.MERCH.h
          : layoutHeightOf(shown, box, streamTickerBarsOf(event))
        const outline = interactive && selectedElementKey === key ? '3px solid rgba(59,130,246,0.7)' : undefined
        const handleSize = resizeHandleSize(box.glyphScale)
        const boxH = isPoll ? measuredHeights?.[key] ?? ELEMENT_BASE[el.elementKind].h : layoutHeight
        const boxTop = isPoll ? -boxH / 2 : 0
        const boundaryPoll = interactive && selectedElementKey === key &&
          onBoundaryPointerDown !== undefined && el.elementKind === 'POLL'
          ? event.polls.find((p) =>
            p.pollId === el.elementId && p.template === 'TICKER'
          ) ?? null
          : null
        let boundaryCuts: number[] = []
        if (boundaryPoll !== null) {
          const effectiveWeights = pollWeightsPreview !== null && pollWeightsPreview.pollId === boundaryPoll.pollId
            ? pollWeightsPreview.weights
            : boundaryPoll.tickerBlockWeights ?? null
          const fractions = tickerBlockFractions(effectiveWeights, boundaryPoll.options.length, box.layoutWidth)
          let acc = 0
          boundaryCuts = fractions.slice(0, -1).map((f) => { acc += f; return acc })
        }
        const boundaryLocked = boundaryPoll !== null && boundaryPoll.status === 'ACTIVE'
        const boundaryLineW = Math.max(1, 2 / box.glyphScale)
        const frame = (
          <div
            key={key}
            onPointerDown={interactive && onElementPointerDown !== undefined
              ? (e) => onElementPointerDown(e, el)
              : undefined}
            onPointerMove={interactive ? onElementPointerMove : undefined}
            onPointerUp={interactive ? onElementPointerUp : undefined}
            onPointerCancel={interactive ? onElementPointerCancel : undefined}
            onDragStart={(e) => { e.preventDefault() }}
            style={{
              position: 'absolute',
              left: shown.x,
              top: shown.y,
              width: box.layoutWidth,
              height: layoutHeight,
              zIndex: item != null ? undefined : el.z,
              transform: `scale(${box.glyphScale})`,
              transformOrigin: 'top left',
              cursor: interactive ? 'move' : undefined,
              touchAction: 'none',
              userSelect: 'none',
              outline: isPoll ? undefined : outline,
              ...(item != null && item.glideMs > 0
                ? { transition: isClipKind ? `${glideTransitionOf(item.glideMs)}, ${clipGlideTransitionOf(item.glideMs)}` : glideTransitionOf(item.glideMs) }
                : null)
            }}
          >
            {isPoll && outline !== undefined && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  width: '100%',
                  top: boxTop,
                  height: boxH,
                  outline,
                  pointerEvents: 'none'
                }}
              />
            )}
            {boundaryPoll !== null && onBoundaryPointerDown !== undefined &&
              boundaryCuts.map((cut, b) => (
                <div
                  key={`boundary-${b}`}
                  onPointerDown={(e) => onBoundaryPointerDown(e, el, b)}
                  onPointerMove={onBoundaryPointerMove}
                  onPointerUp={onBoundaryPointerUp}
                  onPointerCancel={onBoundaryPointerCancel}
                  onDragStart={(e) => { e.preventDefault() }}
                  title={boundaryLocked
                    ? 'Stop the poll before reshaping its blocks.'
                    : 'Drag to reshape the neighboring blocks'}
                  style={{
                    position: 'absolute',
                    left: cut * box.layoutWidth - handleSize / 2,
                    top: boxTop,
                    width: handleSize,
                    height: boxH,
                    cursor: boundaryLocked ? 'not-allowed' : 'col-resize',
                    opacity: boundaryLocked ? 0.4 : undefined,
                    touchAction: 'none',
                    userSelect: 'none',
                    zIndex: 1
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: `calc(50% - ${boundaryLineW / 2}px)`,
                      top: 0,
                      width: boundaryLineW,
                      height: '100%',
                      background: 'rgba(59,130,246,0.9)',
                      pointerEvents: 'none'
                    }}
                  />
                </div>
              ))}
            {interactive && selectedElementKey === key && onResizePointerDown !== undefined &&
              RESIZE_HANDLES
                .filter(({ handle }) => {
                  if (isPoll || isMerch || isTicker || isTickerBar) return handle !== 'n' && handle !== 's'
                  return true
                })
                .map(({ handle, fx, fy, cursor }) => (
                  <div
                    key={handle}
                    onPointerDown={(e) => onResizePointerDown(e, el, handle)}
                    onPointerMove={onResizePointerMove}
                    onPointerUp={onResizePointerUp}
                    onPointerCancel={onResizePointerCancel}
                    onDragStart={(e) => { e.preventDefault() }}
                    title='Drag to resize'
                    style={{
                      position: 'absolute',
                      left: fx * box.layoutWidth - handleSize / 2,
                      top: boxTop + fy * boxH - handleSize / 2,
                      width: handleSize,
                      height: handleSize,
                      cursor,
                      touchAction: 'none',
                      userSelect: 'none',
                      background: '#fff',
                      border: `${Math.max(1, handleSize / 7)}px solid rgba(59,130,246,0.9)`,
                      zIndex: 1
                    }}
                  />
                ))}
            {isPoll && onMeasure !== undefined
              ? (
                <div
                  style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}
                  ref={(node) => {
                    const target = node?.firstElementChild
                    if (!(target instanceof HTMLElement)) return
                    onMeasure(key, target.offsetHeight)
                    const ro = new ResizeObserver(() => onMeasure(key, target.offsetHeight))
                    ro.observe(target)
                    return () => ro.disconnect()
                  }}
                >
                  <SceneElementContent element={el} event={event} streamChats={streamChats} streamWalls={streamWalls} accountBoards={accountBoards} layoutWidth={box.layoutWidth} layoutHeight={layoutHeight} pollWeightsPreview={pollWeightsPreview} />
                </div>
                )
              : <SceneElementContent element={el} event={event} streamChats={streamChats} streamWalls={streamWalls} accountBoards={accountBoards} layoutWidth={box.layoutWidth} layoutHeight={layoutHeight} pollWeightsPreview={pollWeightsPreview} onMerchMeasure={isMerch && onMeasure != null ? (h) => onMeasure(key, h) : undefined} onMerchEnvelope={(isMerch || isTicker) && (onMerchEnvelope != null || presence != null) ? (env) => { onMerchEnvelope?.(el.elementId, env); if (item != null) presence?.onContentReady(item.key) } : undefined} onTickerBarReady={isTickerBar && item != null && presence != null ? () => presence.onContentReady(item.key) : undefined} clipGlideMs={isClipKind && item != null ? item.glideMs : 0} />}
          </div>
        )
        return item == null ? frame : <PresenceLayer key={item.key} item={item}>{frame}</PresenceLayer>
      })}
      {guides !== null && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 9999 }}>
          {guides.vertical !== null && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: guides.vertical - guides.thickness / 2,
                width: guides.thickness,
                height: canvasHeight,
                background: GUIDE_COLOR
              }}
            />
          )}
          {guides.horizontal !== null && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: guides.horizontal - guides.thickness / 2,
                height: guides.thickness,
                width: canvasWidth,
                background: GUIDE_COLOR
              }}
            />
          )}
        </div>
      )}
    </ScaledStage>
  )
}

interface AddCandidate { kind: ElementKind, id: string, name: string }

const ADD_GROUPS: Array<{ id: string, kind: ElementKind, label: string }> = [
  { id: 'POLL', kind: 'POLL', label: 'Polls' },
  { id: 'TRIVIA', kind: 'TRIVIA', label: 'Trivia questions' },
  { id: 'TUG', kind: 'TUG', label: 'Tug of wars' },
  { id: 'BOARD', kind: 'BOARD', label: 'Scoreboards' },
  { id: 'CHAT', kind: 'CHAT', label: 'Chat overlays' },
  { id: 'EMOTEWALL', kind: 'EMOTEWALL', label: 'Emote walls' },
  { id: 'TICKER_BAR', kind: 'TICKER_BAR', label: 'Message tickers' },
  { id: 'MERCH', kind: 'MERCH', label: 'Merch boards' },
  { id: 'MERCH_TICKER', kind: 'MERCH_TICKER', label: 'Merch tickers' },
  { id: 'CAMERA_CONTROL', kind: 'CAMERA_CONTROL', label: 'Camera controls' },
  { id: 'GIVEAWAY', kind: 'GIVEAWAY', label: 'Giveaways' }
]

const AddElementMenu = ({ candidates, disabled, merchVisible, camCtlVisible, tickerBarsVisible = false, onAdd }: {
  candidates: AddCandidate[]
  disabled: boolean
  merchVisible: boolean
  camCtlVisible: boolean
  tickerBarsVisible?: boolean
  onAdd: (candidate: AddCandidate) => void
}): JSX.Element => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  return (
    <div ref={ref} className='relative'>
      <button
        type='button'
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
      >
        <PlusIcon className='size-3.5' aria-hidden='true' />
        Add element
      </button>
      {open && (
        <div className='absolute right-0 z-20 mt-1 max-h-72 w-64 overflow-y-auto rounded-lg border border-white/10 bg-dm-bg-secondary p-2 shadow-xl'>
          {candidates.length === 0 && (
            <p className='px-2 py-3 text-xs text-dm-text-secondary'>
              Everything in this stream is already in the scene.
            </p>
          )}
          {ADD_GROUPS.filter(({ kind }) => {
            if (kind === 'MERCH' || kind === 'MERCH_TICKER') return merchVisible
            if (kind === 'CAMERA_CONTROL' || kind === 'GIVEAWAY') return camCtlVisible
            if (kind === 'TICKER_BAR') return tickerBarsVisible
            return true
          }).map(({ id, kind, label }) => {
            const group = candidates.filter((c) => c.kind === kind)
            if (group.length === 0) return null
            return (
              <div key={id} className='mb-1'>
                <p className='px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>{label}</p>
                {group.map((c) => (
                  <button
                    key={c.id}
                    type='button'
                    onClick={() => { onAdd(c); setOpen(false) }}
                    className='block w-full truncate rounded-md px-2 py-1.5 text-left text-sm text-dm-text-primary hover:bg-white/5'
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const SceneRenameInput = ({ initial, busy, onSave, onCancel }: {
  initial: string
  busy: boolean
  onSave: (name: string) => void
  onCancel: () => void
}): JSX.Element => {
  const [name, setName] = useState(initial)
  return (
    <>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onSave(name)
          }
          if (e.key === 'Escape') onCancel()
        }}
        maxLength={80}
        autoFocus
        className='min-w-0 flex-1 rounded border border-white/10 bg-dm-bg-accent/40 px-2 py-1 text-xs text-dm-text-primary focus:border-brand focus:outline-none'
      />
      <button
        type='button'
        onClick={() => onSave(name)}
        disabled={busy}
        title='Save name'
        className='rounded p-1 text-emerald-400 hover:bg-white/5'
      >
        {busy ? <LoadingSpinner /> : <CheckIcon className='size-3.5' aria-hidden='true' />}
      </button>
      <button
        type='button'
        onClick={onCancel}
        title='Cancel rename'
        className='rounded p-1 text-dm-text-accent hover:bg-white/5'
      >
        <XMarkIcon className='size-3.5' aria-hidden='true' />
      </button>
    </>
  )
}

type ControlAction = 'start' | 'stop' | 'reset' | 'calculate' | 'publish' | 'close' | 'reveal' | `disqualify:${string}`

const ControlButtons = ({ isActive, busy, locked, onStart, onStop, onReset }: {
  isActive: boolean
  busy: ControlAction | null
  locked: boolean
  onStart: () => void
  onStop: () => void
  onReset: () => void
}): JSX.Element => (
  <div className='grid grid-cols-3 gap-2'>
    <button
      type='button'
      onClick={onStart}
      disabled={busy !== null || isActive || locked}
      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50'
    >
      {busy === 'start' ? <LoadingSpinner /> : <PlayCircleIcon className='size-4' aria-hidden='true' />}
      Start
    </button>
    <button
      type='button'
      onClick={onStop}
      disabled={busy !== null || !isActive || locked}
      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-400 px-3 py-2 text-xs font-semibold text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50'
    >
      {busy === 'stop' ? <LoadingSpinner /> : <StopCircleIcon className='size-4' aria-hidden='true' />}
      Stop
    </button>
    <button
      type='button'
      onClick={onReset}
      disabled={busy !== null || locked}
      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-red-500 px-3 py-2 text-xs font-semibold text-black hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-50'
    >
      {busy === 'reset' ? <LoadingSpinner /> : <ArrowPathIcon className='size-4' aria-hidden='true' />}
      Reset
    </button>
  </div>
)

interface ScoreRow {
  competitorId: string
  name: string
  nameTextColor: string
  scoreTextColor: string
  imageUrl: string | null
  scoreImageUrl: string | null
  score: number
  originalScore: number
  originalNameTextColor: string
  originalScoreTextColor: string
}

const toScoreRow = (c: Competitor): ScoreRow => ({
  competitorId: c.competitorId,
  name: c.name,
  nameTextColor: c.nameTextColor,
  scoreTextColor: c.scoreTextColor,
  imageUrl: c.imageUrl,
  scoreImageUrl: c.scoreImageUrl,
  score: c.score,
  originalScore: c.score,
  originalNameTextColor: c.nameTextColor,
  originalScoreTextColor: c.scoreTextColor
})

const BoardScoreControls = ({ board, locked, auth0Token, setEvent }: {
  board: ScoreboardSummary
  locked: boolean
  auth0Token: string | undefined
  setEvent: React.Dispatch<React.SetStateAction<EventDetail | null>>
}): JSX.Element => {
  const [rows, setRows] = useState<ScoreRow[]>(() => board.competitors.map(toScoreRow))
  const [busy, setBusy] = useState<'save' | 'reset' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const seedKey = useMemo(
    () => `${board.boardId}:${board.competitors.map((c) => c.competitorId).join(',')}`,
    [board]
  )
  useEffect(() => {
    setRows(board.competitors.map(toScoreRow))
    setError(null)
  }, [seedKey])

  const updateScore = (idx: number, score: number): void => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, score } : r))
  }

  const updateColors = (
    idx: number,
    patch: Partial<Pick<ScoreRow, 'nameTextColor' | 'scoreTextColor'>>
  ): void => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r))
  }

  const rowIsDirty = (r: ScoreRow): boolean =>
    r.score !== r.originalScore ||
    r.nameTextColor !== r.originalNameTextColor ||
    r.scoreTextColor !== r.originalScoreTextColor

  const isDirty = rows.some(rowIsDirty)

  const saveScores = async (forceZero = false): Promise<void> => {
    if (auth0Token === undefined || busy !== null || locked) return
    const changed = forceZero
      ? rows.filter((r) => r.score !== 0)
      : rows.filter(rowIsDirty)
    if (changed.length === 0) return
    setBusy(forceZero ? 'reset' : 'save')
    setError(null)
    try {
      const { scoreboard } = await graphqlRequest<{ scoreboard: { competitors: Competitor[] } }>(
        updateCompetitorsMutation,
        {
          boardId: board.boardId,
          updates: changed.map((r) => ({
            competitorId: r.competitorId,
            name: r.name,
            score: forceZero ? 0 : r.score,
            nameTextColor: r.nameTextColor,
            scoreTextColor: r.scoreTextColor,
            imageUrl: r.imageUrl,
            scoreImageUrl: r.scoreImageUrl
          }))
        },
        auth0Token
      )
      const savedById = new Map(scoreboard.competitors.map((c) => [c.competitorId, c]))
      setRows((prev) => prev.map((row) => {
        const saved = savedById.get(row.competitorId)
        if (saved === undefined) return row
        return {
          ...row,
          score: saved.score,
          originalScore: saved.score,
          nameTextColor: saved.nameTextColor,
          originalNameTextColor: saved.nameTextColor,
          scoreTextColor: saved.scoreTextColor,
          originalScoreTextColor: saved.scoreTextColor
        }
      }))
      setEvent((prev) => {
        if (prev === null) return prev
        return {
          ...prev,
          scoreboards: prev.scoreboards.map((s) => {
            if (s.boardId !== board.boardId) return s
            return {
              ...s,
              competitors: s.competitors.map((c) => {
                const saved = savedById.get(c.competitorId)
                return saved === undefined ? c : { ...c, ...saved }
              })
            }
          })
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scores')
    } finally {
      setBusy(null)
    }
  }

  const handleReset = (): void => {
    const ok = globalThis.confirm(`Reset all competitor scores on "${board.name}" to zero?`)
    if (!ok) return
    void saveScores(true)
  }

  return (
    <div className='flex flex-col gap-2'>
      {rows.length === 0
        ? (
          <p className='rounded-md border border-dashed border-white/10 px-3 py-4 text-center text-xs text-dm-text-accent'>
            This scoreboard has no competitors yet. Add competitors on the editor page.
          </p>
          )
        : (
          <div className='space-y-1.5'>
            {rows.map((row, idx) => (
              <div key={row.competitorId} className='rounded-md border border-white/5 bg-dm-bg-accent/30 p-2'>
                <div className='flex items-center gap-1.5'>
                  <span className='min-w-0 flex-1 truncate text-xs text-dm-text-primary'>{row.name}</span>
                  <button
                    type='button'
                    onClick={() => updateScore(idx, Math.max(0, row.score - 1))}
                    disabled={locked}
                    className='inline-flex size-6 shrink-0 items-center justify-center rounded-md border border-white/10 text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
                    aria-label={`Decrease ${row.name} score`}
                  >
                    <MinusIcon className='size-3' aria-hidden='true' />
                  </button>
                  <NumberInput
                    value={row.score}
                    min={0}
                    onCommit={(n) => updateScore(idx, n)}
                    className='w-14 shrink-0 rounded-md border border-white/10 bg-dm-bg-accent/40 px-1.5 py-1 text-center font-mono text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                    aria-label={`${row.name} score`}
                  />
                  <button
                    type='button'
                    onClick={() => updateScore(idx, row.score + 1)}
                    disabled={locked}
                    className='inline-flex size-6 shrink-0 items-center justify-center rounded-md border border-white/10 text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
                    aria-label={`Increase ${row.name} score`}
                  >
                    <PlusIcon className='size-3' aria-hidden='true' />
                  </button>
                </div>
                <fieldset disabled={locked} className='mt-2 grid grid-cols-1 gap-2 md:grid-cols-2'>
                  <ColorInput
                    label='Name text color'
                    value={row.nameTextColor}
                    onChange={(v) => updateColors(idx, { nameTextColor: v })}
                  />
                  <ColorInput
                    label='Score text color'
                    value={row.scoreTextColor}
                    onChange={(v) => updateColors(idx, { scoreTextColor: v })}
                  />
                </fieldset>
              </div>
            ))}
          </div>
          )}
      <button
        type='button'
        onClick={() => { void saveScores() }}
        disabled={busy !== null || !isDirty || rows.length === 0 || locked}
        className='inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-amber-400 px-3 py-2 text-xs font-semibold text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50'
      >
        {busy === 'save' ? <LoadingSpinner /> : <SignalIcon className='size-4' aria-hidden='true' />}
        Save &amp; apply scores
      </button>
      <button
        type='button'
        onClick={handleReset}
        disabled={busy !== null || rows.length === 0 || locked}
        className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-red-500/40 px-3 py-2 text-xs font-medium text-red-300 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-50'
      >
        {busy === 'reset' ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
        Reset scores
      </button>
      {error !== null && (
        <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
      )}
    </div>
  )
}

const tickerWeightsEq = (a: number[] | null, b: number[] | null): boolean => {
  if (a === null || b === null) return a === b
  return a.length === b.length && a.every((w, i) => w === b[i])
}

interface LayoutEditMeta {
  label: string
  elementKey: string | null
  coalesceKey: string | null
}

const OverlayEditorPage = ({ kind, overlayLinkK }: {
  kind: CompositeOverlayKind
  overlayLinkK: string | null
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const sceneCap = MAX_SCENES_PER_OVERLAY
  const { socketToken } = useSocketToken()
  const { event, setEvent, refetchEvent } = useEventDetail()
  const isEnded = event.status === 'ENDED'
  const overlayUrl = buildOverlayShortUrl(event.slug, COMPOSITE_OVERLAY_SEGMENT[kind], overlayLinkK)

  const [overlayCore, setOverlayCore] = useState<OverlayCore | null>(null)
  const [scenes, setScenes] = useState<Record<string, OverlayScene>>({})
  const [loadError, setLoadError] = useState<string | null>(null)

  const [accountListsSettled, setAccountListsSettled] = useState({ boards: false })

  const streamChats = streamChatsOf(event)
  const streamWalls = streamWallsOf(event)
  const streamTickerBars = streamTickerBarsOf(event)

  const merchVisible = useMerchSurfaceVisible()
  const { staffUncapped } = useEntitlements()
  const tickerBarsVisible = useTickerBarSurfaceVisible() === 'visible'
  const [accountBoards, setAccountBoards] = useState<MerchOverlaySummary[]>([])
  useEffect(() => {
    if (auth0Token === undefined || !merchVisible) return
    let cancelled = false
    graphqlRequest<{ overlays: MerchOverlaySummary[] }>(listMyMerchOverlaysQuery, {}, auth0Token)
      .then(({ overlays }) => {
        if (!cancelled) setAccountBoards(overlays)
        if (!cancelled) setAccountListsSettled((s) => s.boards ? s : { ...s, boards: true })
      })
      .catch(() => {
        if (!cancelled) setAccountListsSettled((s) => s.boards ? s : { ...s, boards: true })
      })
    return () => { cancelled = true }
  }, [auth0Token, merchVisible])

  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null)
  const [selectedElementKey, setSelectedElementKey] = useState<string | null>(null)
  const [renamingSceneId, setRenamingSceneId] = useState<string | null>(null)
  const [railBusy, setRailBusy] = useState<string | null>(null) // sceneId or 'create'
  const [railError, setRailError] = useState<string | null>(null)
  const [elementsSaving, setElementsSaving] = useState(false)
  const [queueError, setQueueError] = useState<string | null>(null)
  const [liveBusy, setLiveBusy] = useState<'take' | 'fade' | 'clear' | null>(null)
  const [liveError, setLiveError] = useState<string | null>(null)
  const [removeFailed, setRemoveFailed] = useState(false)
  const [chatGateStatus, setChatGateStatus] = useState<ChatGateCounts>(EMPTY_CHAT_GATE_COUNTS)
  const removeTokenRef = useRef(auth0Token)
  removeTokenRef.current = auth0Token
  const chatLineRemover = useMemo(() => createChatLineRemover({
    send: async (removal: ChatLineRemoval) => {
      const token = removeTokenRef.current
      if (token == null) throw new Error('signed out')
      return await graphqlRequest<{ StreamToolsRemoveChatLine: { removed: boolean } }>(
        removeChatLineMutation, removeChatLineVariablesOf(removal), token
      )
    },
    onFailure: () => setRemoveFailed(true),
    onSuccess: () => setRemoveFailed(false)
  }), [])
  useEffect(() => () => chatLineRemover.dispose(), [chatLineRemover])
  const handleRemoveChatLine = useCallback((removal: ChatLineRemoval): void => {
    chatLineRemover.remove(removal)
  }, [chatLineRemover])
  const { streamToolsUser } = useAuth()
  const chatLineRemoveOffered = !isEnded || streamToolsUser?.superuser === true
  const [controlsBusy, setControlsBusy] = useState<ControlAction | null>(null)
  const [controlsError, setControlsError] = useState<string | null>(null)
  const [triviaRevealNowFor, setTriviaRevealNowFor] = useState<string | null>(null)
  const [ytWarn, setYtWarn] = useState<{ kind: 'POLL' | 'TUG' | 'TRIVIA', id: string, seq: number } | null>(null)
  const armYouTubeWarning = (kind: 'POLL' | 'TUG' | 'TRIVIA', id: string): void => {
    setYtWarn((prev) => ({ kind, id, seq: (prev?.seq ?? 0) + 1 }))
  }
  const [pollWeightsPreview, setPollWeightsPreview] = useState<{ pollId: string, weights: number[] | null, baseline: number[] | null } | null>(null)
  const [weightsBusy, setWeightsBusy] = useState(false)
  const [weightsError, setWeightsError] = useState<string | null>(null)

  const [histories, setHistories] = useState<SceneHistories>({})
  const macPlatform = useMemo(() => isMacPlatform(), [])

  const weightsDirty = useMemo(
    () => pollWeightsPreview !== null && !tickerWeightsEq(pollWeightsPreview.weights, pollWeightsPreview.baseline),
    [pollWeightsPreview]
  )

  useEffect(() => {
    if (pollWeightsPreview === null) return
    const row = event.polls.find((p) => p.pollId === pollWeightsPreview.pollId)
    if (row === undefined || row.status !== 'ACTIVE') return
    setPollWeightsPreview(null)
    setHistories((prev) => clearDomainEverywhere(prev, 'weights', row.pollId))
    setWeightsError('This poll went live, so the unsaved block-width changes were dropped. Stop the poll to reshape its blocks.')
  }, [event.polls, pollWeightsPreview])

  useEffect(() => {
    setHistories((prev) => pruneWeights(prev, (pollId) => {
      const row = event.polls.find((p) => p.pollId === pollId)
      return row != null && row.status !== 'ACTIVE' && row.template === 'TICKER'
    }))
  }, [event.polls])

  const [draft, setDraft] = useState<{ sceneId: string, elements: SceneElement[] } | null>(null)

  const [contentHeights, setContentHeights] = useState<Record<string, number>>({})
  const [merchEnvelopes, setMerchEnvelopes] = useState<Record<string, MerchEnvelope>>({})
  const handleMerchEnvelope = useCallback((merchOverlayId: string, envelope: MerchEnvelope): void => {
    setMerchEnvelopes((prev) => ({ ...prev, [merchOverlayId]: envelope }))
  }, [])
  const handleMeasure = useCallback((key: string, height: number): void => {
    setContentHeights((prev) => prev[key] === height ? prev : { ...prev, [key]: height })
  }, [])

  const withDerivedBox = useCallback((el: SceneElement): SceneElement => {
    if (el.w !== null && el.h !== null) return el
    const box = deriveDefaultBox(el.elementKind, el.scale)
    let h = isCenterAnchoredKind(el.elementKind) || el.elementKind === 'MERCH'
      ? Math.round((contentHeights[elementKeyOf(el)] ?? ELEMENT_BASE[el.elementKind].h) * el.scale)
      : box.h
    if (el.elementKind === 'TICKER_BAR') h = Math.round(tickerBarUnscaledHeightOf(streamTickerBars, el.elementId) * el.scale)
    return { ...el, w: box.w, h }
  }, [contentHeights, streamTickerBars])

  const draftElementsFor = useCallback((scene: OverlayScene): SceneElement[] => {
    if (draft !== null && draft.sceneId === scene.sceneId) return draft.elements
    return scene.elements.map(withDerivedBox)
  }, [draft, withDerivedBox])

  const visualRectOf = useCallback((el: SceneElement): Rect => {
    const box = resolveElementBox(el)
    const autoGrow = isCenterAnchoredKind(el.elementKind) || el.elementKind === 'MERCH'
    let vh = autoGrow
      ? Math.round((contentHeights[elementKeyOf(el)] ?? ELEMENT_BASE[el.elementKind].h) * box.glyphScale)
      : el.elementKind === 'MERCH_TICKER'
        ? Math.round(ELEMENT_BASE.MERCH_TICKER.h * box.glyphScale)
        : box.h
    if (el.elementKind === 'TICKER_BAR') vh = Math.round(tickerBarUnscaledHeightOf(streamTickerBars, el.elementId) * box.glyphScale)
    const vw = el.elementKind === 'MERCH' || el.elementKind === 'MERCH_TICKER' || el.elementKind === 'TICKER_BAR'
      ? Math.round(box.layoutWidth * box.glyphScale)
      : box.w
    const top = isCenterAnchoredKind(el.elementKind) ? el.y - vh / 2 : el.y
    return { left: el.x, top, right: el.x + vw, bottom: top + vh }
  }, [contentHeights, streamTickerBars])

  const isDirty = useMemo(() => {
    if (draft === null) return false
    const baseline = scenes[draft.sceneId]
    if (baseline === undefined) return false
    return !elementsEqual(draft.elements, baseline.elements.map(withDerivedBox))
  }, [draft, scenes, withDerivedBox])

  useEffect(() => {
    if (draft !== null && scenes[draft.sceneId] === undefined) setDraft(null)
  }, [draft, scenes])

  useEffect(() => {
    setHistories((prev) => pruneScenes(prev, (sceneId) => scenes[sceneId] != null))
  }, [scenes])

  useEffect(() => {
    if (!isDirty && !weightsDirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    globalThis.addEventListener('beforeunload', onBeforeUnload)
    return () => globalThis.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty, weightsDirty])

  useEffect(() => {
    if (!isDirty && !weightsDirty) return
    const onClickCapture = (e: MouseEvent): void => {
      const target = e.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a[href]')
      if (!(anchor instanceof HTMLAnchorElement)) return
      if (anchor.target === '_blank') return
      const url = new URL(anchor.href, globalThis.location.href)
      if (url.origin !== globalThis.location.origin) return
      if (url.pathname === globalThis.location.pathname && url.search === globalThis.location.search && url.hash !== '') return
      if (globalThis.confirm('You have unsaved changes — leave without saving?')) return
      e.preventDefault()
      e.stopPropagation()
    }
    document.addEventListener('click', onClickCapture, true)
    return () => document.removeEventListener('click', onClickCapture, true)
  }, [isDirty, weightsDirty])

  const [liveTake, setLiveTake] = useState<LiveTake>(NO_LIVE_TAKE)
  const applyOverlayCore = useCallback((core: OverlayCore, transition: SceneTransition = 'CUT'): void => {
    setOverlayCore((prev) => prev !== null && prev.version >= core.version ? prev : core)
    setLiveTake((prev) => foldLiveTake(prev, core, transition))
  }, [])

  const upsertScene = useCallback((scene: OverlayScene): void => {
    setScenes((prev) => {
      const existing = prev[scene.sceneId]
      if (existing !== undefined && existing.version >= scene.version) return prev
      return { ...prev, [scene.sceneId]: scene }
    })
  }, [])

  const removeScene = useCallback((sceneId: string): void => {
    setScenes((prev) => {
      if (prev[sceneId] === undefined) return prev
      const { [sceneId]: _dropped, ...rest } = prev
      return rest
    })
    setOverlayCore((prev) => {
      if (prev === null) return prev
      const inOrder = prev.sceneOrder.includes(sceneId)
      const wasLive = prev.liveSceneId === sceneId
      if (!inOrder && !wasLive) return prev
      return {
        ...prev,
        sceneOrder: inOrder ? prev.sceneOrder.filter((id) => id !== sceneId) : prev.sceneOrder,
        liveSceneId: wasLive ? null : prev.liveSceneId,
        liveScene: wasLive ? null : prev.liveScene
      }
    })
    setSelectedSceneId((prev) => prev === sceneId ? null : prev)
  }, [])

  const adoptFullOverlay = useCallback((overlay: EventOverlay): void => {
    const { scenes: fetched, ...core } = overlay
    applyOverlayCore(core)
    fetched.forEach(upsertScene)
  }, [applyOverlayCore, upsertScene])

  const loadedScopeRef = useRef<string | null>(null)
  useEffect(() => {
    const scope = `${event.eventId}:${kind}`
    if (auth0Token === undefined || loadedScopeRef.current === scope) return
    const eventId = event.eventId
    loadedScopeRef.current = scope
    setOverlayCore(null)
    setLiveTake(NO_LIVE_TAKE)
    setScenes({})
    setSelectedSceneId(null)
    setSelectedElementKey(null)
    setRenamingSceneId(null)
    setDraft(null)
    setHistories({})
    setLoadError(null)
    const adoptForKind = (overlay: EventOverlay): void => {
      if (overlay.overlayId !== kind) {
        setLoadError('Server returned the wrong overlay kind — StreamToolsGetOverlay/CreateOverlay may be missing $kind support.')
        return
      }
      adoptFullOverlay(overlay)
    }
    const load = async (): Promise<void> => {
      try {
        const { overlay } = await graphqlRequest<{ overlay: EventOverlay | null }>(
          getOverlayQuery, { eventId, kind }, auth0Token
        )
        if (loadedScopeRef.current !== scope) return
        if (overlay !== null) {
          adoptForKind(overlay)
          return
        }
        if (isEnded) {
          setLoadError(`This stream ended before a ${KIND_NOUN[kind]} overlay was created.`)
          return
        }
        const created = await graphqlRequest<{ overlay: EventOverlay }>(
          createOverlayMutation, { eventId, kind }, auth0Token
        )
        if (loadedScopeRef.current !== scope) return
        adoptForKind(created.overlay)
      } catch (err) {
        if (loadedScopeRef.current !== scope) return
        setLoadError(err instanceof Error ? err.message : 'Failed to load overlay')
      }
    }
    void load()
  }, [auth0Token, event.eventId, kind, isEnded, adoptFullOverlay])

  const resyncOverlay = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    const eventId = event.eventId
    const scope = `${eventId}:${kind}`
    try {
      const { overlay } = await graphqlRequest<{ overlay: EventOverlay | null }>(
        getOverlayQuery, { eventId, kind }, auth0Token
      )
      if (loadedScopeRef.current !== scope) return
      if (overlay !== null && overlay.overlayId === kind) adoptFullOverlay(overlay)
    } catch {
    }
  }, [auth0Token, event.eventId, kind, adoptFullOverlay])

  useOverlayStructureEvents(
    `${event.slug}:overlay:${kind.toLowerCase()}:admin`,
    socketToken ?? null,
    kind,
    {
      onOverlay: useCallback((env: OverlayStructureEnvelope) => {
        applyOverlayCore(env.overlay, env.transition ?? 'CUT')
        if (env.scene !== null) upsertScene(env.scene)
        if (env.deletedSceneId !== null) removeScene(env.deletedSceneId)
      }, [applyOverlayCore, upsertScene, removeScene]),
      onConnected: useCallback(() => { void resyncOverlay() }, [resyncOverlay])
    }
  )

  useChannelEvents<PollSummary>(
    `${event.slug}:polls:admin`,
    socketToken ?? null,
    {
      onUpdate: useCallback((incoming: PollSummary) => {
        setEvent((prev) => {
          if (prev === null) return prev
          const idx = prev.polls.findIndex((p) => p.pollId === incoming.pollId)
          if (idx === -1) return { ...prev, polls: [...prev.polls, incoming] }
          if (prev.polls[idx].version > incoming.version) return prev
          const next = prev.polls.slice()
          next[idx] = incoming
          return { ...prev, polls: next }
        })
      }, [setEvent]),
      onDelete: useCallback((pollId: string) => {
        setEvent((prev) => prev === null
          ? prev
          : { ...prev, polls: prev.polls.filter((p) => p.pollId !== pollId) })
      }, [setEvent])
    }
  )
  const triviaNowMs = useTriviaGraceTick(event.trivias)
  useChannelEvents<TugSummary>(
    `${event.slug}:tugs:admin`,
    socketToken ?? null,
    {
      onUpdate: useCallback((incoming: TugSummary) => {
        setEvent((prev) => {
          if (prev === null) return prev
          const idx = prev.tugs.findIndex((t) => t.tugId === incoming.tugId)
          if (idx === -1) return { ...prev, tugs: [...prev.tugs, incoming] }
          if (prev.tugs[idx].version > incoming.version) return prev
          const next = prev.tugs.slice()
          next[idx] = incoming
          return { ...prev, tugs: next }
        })
      }, [setEvent]),
      onDelete: useCallback((tugId: string) => {
        setEvent((prev) => prev === null
          ? prev
          : { ...prev, tugs: prev.tugs.filter((t) => t.tugId !== tugId) })
      }, [setEvent])
    }
  )
  useChannelEvents<ScoreboardSummary>(
    `${event.slug}:scoreboards:admin`,
    socketToken ?? null,
    {
      onUpdate: useCallback((incoming: ScoreboardSummary) => {
        setEvent((prev) => {
          if (prev === null) return prev
          const idx = prev.scoreboards.findIndex((s) => s.boardId === incoming.boardId)
          if (idx === -1) return { ...prev, scoreboards: [...prev.scoreboards, incoming] }
          if (prev.scoreboards[idx].version > incoming.version) return prev
          const next = prev.scoreboards.slice()
          next[idx] = incoming
          return { ...prev, scoreboards: next }
        })
      }, [setEvent]),
      onDelete: useCallback((boardId: string) => {
        setEvent((prev) => prev === null
          ? prev
          : { ...prev, scoreboards: prev.scoreboards.filter((s) => s.boardId !== boardId) })
      }, [setEvent])
    }
  )
  useChannelEvents<CameraControlSummary>(
    `${event.slug}:camctls:admin`,
    socketToken ?? null,
    {
      onUpdate: useCallback((incoming: CameraControlSummary) => {
        setEvent((prev) => {
          if (prev === null) return prev
          const idx = prev.cameraControls.findIndex((c) => c.camControlId === incoming.camControlId)
          if (idx === -1) return { ...prev, cameraControls: [...prev.cameraControls, incoming] }
          if (prev.cameraControls[idx].version > incoming.version) return prev
          const next = prev.cameraControls.slice()
          next[idx] = incoming
          return { ...prev, cameraControls: next }
        })
      }, [setEvent]),
      onDelete: useCallback((camControlId: string) => {
        setEvent((prev) => prev === null
          ? prev
          : { ...prev, cameraControls: prev.cameraControls.filter((c) => c.camControlId !== camControlId) })
      }, [setEvent])
    }
  )
  useChannelEvents<GiveawaySummary>(
    `${event.slug}:giveaways:admin`,
    socketToken ?? null,
    {
      onUpdate: useCallback((incoming: GiveawaySummary) => {
        setEvent((prev) => {
          if (prev === null) return prev
          const idx = prev.giveaways.findIndex((g) => g.giveawayId === incoming.giveawayId)
          if (idx === -1) return { ...prev, giveaways: [...prev.giveaways, incoming] }
          if (prev.giveaways[idx].version > incoming.version) return prev
          const next = prev.giveaways.slice()
          next[idx] = incoming.draw === undefined
            ? { ...incoming, draw: incoming.hasDraw ? prev.giveaways[idx].draw : null }
            : incoming
          return { ...prev, giveaways: next }
        })
      }, [setEvent]),
      onDelete: useCallback((giveawayId: string) => {
        setEvent((prev) => prev === null
          ? prev
          : { ...prev, giveaways: prev.giveaways.filter((g) => g.giveawayId !== giveawayId) })
      }, [setEvent])
    }
  )
  useTriviaAdminFold(event.slug, socketToken ?? null, setEvent)

  const [localSceneOrder, setLocalSceneOrder] = useState<string[]>([])
  const localOrderScopeRef = useRef<string | null>(null)
  useEffect(() => {
    if (overlayCore === null) return
    const { eventId, overlayId, sceneOrder } = overlayCore
    const scope = `${eventId}:${overlayId}`
    const fresh = localOrderScopeRef.current !== scope
    localOrderScopeRef.current = scope
    const base = fresh ? readStoredSceneOrder(eventId, overlayId) : localSceneOrder
    const next = reconcileSceneOrder(base, sceneOrder)
    const changed = next.length !== base.length || next.some((id, i) => id !== base[i])
    if (changed) writeStoredSceneOrder(eventId, overlayId, next)
    if (fresh || changed) setLocalSceneOrder(next)
  }, [overlayCore, localSceneOrder])

  const fullSceneOrder = useMemo(() => {
    if (overlayCore === null) return []
    return reconcileSceneOrder(localSceneOrder, overlayCore.sceneOrder)
  }, [localSceneOrder, overlayCore])

  const orderedScenes = useMemo(() => {
    return fullSceneOrder
      .map((id) => scenes[id])
      .filter((s): s is OverlayScene => s !== undefined)
  }, [fullSceneOrder, scenes])

  const selectedScene = useMemo(() => {
    if (selectedSceneId !== null) {
      const chosen = scenes[selectedSceneId]
      if (chosen !== undefined) return chosen
    }
    return orderedScenes[0] ?? null
  }, [selectedSceneId, scenes, orderedScenes])

  const liveSnapshot = overlayCore !== null ? overlayCore.liveScene : null
  const liveTakeId = takeIdOf(liveSnapshot)
  const livePresence = useScenePresence({
    takeId: liveTakeId,
    elements: liveSnapshot != null ? liveSnapshot.elements : NO_LIVE_ELEMENTS,
    transition: liveTake.takeId === liveTakeId ? liveTake.transition : 'CUT',
    skipWhenHidden: true
  })
  const hydrateAttemptedForRef = useRef<string | null>(null)
  useEffect(() => {
    if (liveTakeId == null || hydrateAttemptedForRef.current === liveTakeId) return
    const unknown = liveSnapshot?.elements.some((el) =>
      (el.elementKind === 'POLL' && !event.polls.some((p) => p.pollId === el.elementId)) ||
      (el.elementKind === 'TUG' && !event.tugs.some((t) => t.tugId === el.elementId)) ||
      (el.elementKind === 'BOARD' && !event.scoreboards.some((b) => b.boardId === el.elementId)) ||
      (el.elementKind === 'GIVEAWAY' && !event.giveaways.some((g) => g.giveawayId === el.elementId)) ||
      (el.elementKind === 'TRIVIA' && !event.trivias.some((t) => t.triviaId === el.elementId)) ||
      (el.elementKind === 'CHAT' && !streamChats.some((c) => c.chatId === el.elementId)) ||
      (el.elementKind === 'EMOTEWALL' && !streamWalls.some((w) => w.emoteWallId === el.elementId)) ||
      (el.elementKind === 'TICKER_BAR' && !streamTickerBars.some((t) => t.tickerBarId === el.elementId))
    ) ?? false
    if (!unknown) return
    hydrateAttemptedForRef.current = liveTakeId
    void refetchEvent()
      .catch(() => {
      })
  }, [liveTakeId, liveSnapshot, event.polls, event.tugs, event.scoreboards, event.giveaways, event.trivias, streamChats, streamWalls, streamTickerBars, refetchEvent])

  const accountHydrateRef = useRef<{ boards: string | null }>({ boards: null })
  useEffect(() => {
    if (liveTakeId == null || liveSnapshot == null || auth0Token == null) return
    const marks = accountHydrateRef.current
    const els = liveSnapshot.elements
    if (merchVisible && accountListsSettled.boards && marks.boards !== liveTakeId &&
      els.some((el) => (el.elementKind === 'MERCH' || el.elementKind === 'MERCH_TICKER') && !accountBoards.some((b) => b.merchOverlayId === el.elementId))) {
      marks.boards = liveTakeId
      void graphqlRequest<{ overlays: MerchOverlaySummary[] }>(listMyMerchOverlaysQuery, {}, auth0Token)
        .then(({ overlays }) => { setAccountBoards((prev) => withAddedRows(prev, overlays, (b) => b.merchOverlayId)) })
        .catch(() => {
        })
    }
  }, [liveTakeId, liveSnapshot, auth0Token, merchVisible, accountListsSettled, accountBoards])

  const workingElements = useMemo(() => {
    if (selectedScene === null) return null
    if (draft !== null && draft.sceneId === selectedScene.sceneId) return draft.elements
    return selectedScene.elements
  }, [selectedScene, draft])

  const workingScene = useMemo(
    () => workingElements === null ? null : { elements: workingElements },
    [workingElements]
  )

  const selectedElement = useMemo(() => {
    if (workingElements === null || selectedElementKey === null) return null
    return workingElements.find((el) => elementKeyOf(el) === selectedElementKey) ?? null
  }, [workingElements, selectedElementKey])

  const lastDraftRef = useRef<{ sceneId: string | null, dirty: boolean, selectedId: string | null }>({ sceneId: null, dirty: false, selectedId: null })
  useEffect(() => {
    const prev = lastDraftRef.current
    const selectedId = selectedScene != null ? selectedScene.sceneId : null
    lastDraftRef.current = { sceneId: draft != null ? draft.sceneId : null, dirty: isDirty, selectedId }
    const discarded = prev.sceneId
    if (discarded != null && prev.dirty && draft == null && prev.selectedId !== selectedId) {
      setHistories((h) => clearSceneDomain(h, discarded, 'layout'))
    }
  }, [draft, isDirty, selectedScene])

  const addCandidates = useMemo<AddCandidate[]>(() => {
    if (workingElements === null) return []
    const inScene = new Set(workingElements.map(elementKeyOf))
    const all: AddCandidate[] = [
      ...event.polls.map((p): AddCandidate => ({ kind: 'POLL', id: p.pollId, name: elementDisplayName(p.name, p.question) })),
      ...event.trivias.map((t): AddCandidate => ({ kind: 'TRIVIA', id: t.triviaId, name: elementDisplayName(t.name, t.question) })),
      ...event.tugs.map((t): AddCandidate => ({ kind: 'TUG', id: t.tugId, name: elementDisplayName(t.name, t.question) })),
      ...event.scoreboards.map((s): AddCandidate => ({ kind: 'BOARD', id: s.boardId, name: s.name })),
      ...streamElementCandidatesOf({ chatOverlays: streamChats, emoteWalls: streamWalls }).map((c): AddCandidate => ({ kind: c.kind, id: c.id, name: c.name })),
      ...streamTickerBars.map((t): AddCandidate => ({ kind: 'TICKER_BAR', id: t.tickerBarId, name: t.name })),
      ...accountBoards.map((b): AddCandidate => ({
        kind: merchDisplayStyleOf(b) === 'TICKER' ? 'MERCH_TICKER' : 'MERCH',
        id: b.merchOverlayId,
        name: b.name
      })),
      ...event.cameraControls.map((c): AddCandidate => ({ kind: 'CAMERA_CONTROL', id: c.camControlId, name: c.name })),
      ...event.giveaways.map((g): AddCandidate => ({ kind: 'GIVEAWAY', id: g.giveawayId, name: g.name }))
    ]
    return sortByName(all.filter((c) => !inScene.has(elementKeyOf({ elementKind: c.kind, elementId: c.id }))), (c) => c.name, (c) => c.id)
  }, [workingElements, event.polls, event.trivias, event.tugs, event.scoreboards, event.cameraControls, event.giveaways, streamChats, streamWalls, streamTickerBars, accountBoards])

  const handleSelectScene = (sceneId: string): void => {
    if (selectedScene !== null && sceneId === selectedScene.sceneId) return
    if (isDirty && draft !== null) {
      const name = scenes[draft.sceneId]?.name ?? 'this scene'
      const ok = globalThis.confirm(
        `You have unsaved changes in "${name}". Discard them and switch scenes? (Cancel to stay and Save.)`
      )
      if (!ok) return
    }
    setDraft(null)
    setSelectedSceneId(sceneId)
  }

  const handleCreateScene = async (): Promise<void> => {
    if (auth0Token === undefined || overlayCore === null || railBusy !== null || isEnded) return
    if (orderedScenes.length >= sceneCap) return
    setRailBusy('create')
    setRailError(null)
    try {
      const { scene } = await graphqlRequest<{ scene: OverlayScene }>(
        createSceneMutation,
        { eventId: event.eventId, overlayId: overlayCore.overlayId, name: `Scene ${orderedScenes.length + 1}` },
        auth0Token
      )
      upsertScene(scene)
      setOverlayCore((prev) => prev === null || prev.sceneOrder.includes(scene.sceneId)
        ? prev
        : { ...prev, sceneOrder: [...prev.sceneOrder, scene.sceneId] })
      setSelectedSceneId(scene.sceneId)
    } catch (err) {
      setRailError(err instanceof Error ? err.message : 'Failed to create scene')
    } finally {
      setRailBusy(null)
    }
  }

  const handleRenameScene = async (sceneId: string, rawName: string): Promise<void> => {
    const name = rawName.trim()
    if (auth0Token === undefined || overlayCore === null || railBusy !== null || isEnded) return
    if (name === '' || name.length > 80) return
    setRailBusy(sceneId)
    setRailError(null)
    try {
      const { scene } = await graphqlRequest<{ scene: OverlayScene }>(
        renameSceneMutation,
        { eventId: event.eventId, overlayId: overlayCore.overlayId, sceneId, name },
        auth0Token
      )
      upsertScene(scene)
      setRenamingSceneId(null)
    } catch (err) {
      setRailError(err instanceof Error ? err.message : 'Failed to rename scene')
    } finally {
      setRailBusy(null)
    }
  }

  const handleDeleteScene = async (scene: OverlayScene): Promise<void> => {
    if (auth0Token === undefined || overlayCore === null || railBusy !== null || isEnded) return
    const onAir = overlayCore.liveSceneId === scene.sceneId
    const hasDirtyDraft = isDirty && draft !== null && draft.sceneId === scene.sceneId
    const ok = globalThis.confirm(
      `Delete scene "${scene.name}"?` +
      (onAir ? '\n\nThis scene is LIVE — deleting it clears the live overlay.' : '') +
      (hasDirtyDraft ? '\n\nThis scene also has UNSAVED local changes — deleting discards them.' : '')
    )
    if (!ok) return
    setRailBusy(scene.sceneId)
    setRailError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteSceneMutation,
        { eventId: event.eventId, overlayId: overlayCore.overlayId, sceneId: scene.sceneId },
        auth0Token
      )
      if (result.deleted) removeScene(scene.sceneId)
    } catch (err) {
      setRailError(err instanceof Error ? err.message : 'Failed to delete scene')
    } finally {
      setRailBusy(null)
    }
  }

  const handleMoveScene = (sceneId: string, direction: -1 | 1): void => {
    if (overlayCore === null || isEnded) return
    const order = fullSceneOrder
    const from = order.indexOf(sceneId)
    const to = from + direction
    if (from === -1 || to < 0 || to >= order.length) return
    const next = [...order]
    next[from] = order[to]
    next[to] = order[from]
    setLocalSceneOrder(next)
    writeStoredSceneOrder(overlayCore.eventId, overlayCore.overlayId, next)
  }

  const commitDraft = useCallback((sceneId: string, next: SceneElement[], meta: LayoutEditMeta): void => {
    const scene = scenes[sceneId]
    if (scene == null) return
    const entry: LayoutEntry = { domain: 'layout', pollId: null, before: draftElementsFor(scene), after: next, ...meta, at: Date.now() }
    const replaced = draft != null && draft.sceneId !== sceneId && isDirty ? draft.sceneId : null
    setHistories((prev) => recordSceneEdit(replaced != null ? clearSceneDomain(prev, replaced, 'layout') : prev, sceneId, entry))
    setDraft({ sceneId, elements: next })
    setSelectedSceneId((prev) => prev ?? sceneId)
  }, [scenes, draft, isDirty, draftElementsFor])

  const applyDraft = (transform: (els: SceneElement[]) => SceneElement[], meta: LayoutEditMeta): void => {
    if (selectedScene == null || isEnded) return
    commitDraft(selectedScene.sceneId, transform(draftElementsFor(selectedScene)), meta)
  }

  const compactZ = (elements: SceneElement[]): SceneElement[] =>
    [...elements].sort((a, b) => a.z - b.z).map((el, i) => ({ ...el, z: i }))

  const handleAddElement = (candidate: AddCandidate): void => {
    if (selectedScene === null || overlayCore === null || isEnded) return
    if (draftElementsFor(selectedScene).length >= MAX_ELEMENTS_PER_SCENE) return
    const scale = quantizeScale(overlayCore.defaultElementScale ?? FALLBACK_DEFAULT_ELEMENT_SCALE[kind], SCALE_MAX)
    const derived = deriveDefaultBox(candidate.kind, scale)
    const box = candidate.kind === 'TICKER_BAR'
      ? { w: derived.w, h: Math.round(tickerBarUnscaledHeightOf(streamTickerBars, candidate.id) * scale) }
      : derived
    const canvasW = overlayCore.width
    const canvasH = overlayCore.height
    applyDraft((els) => {
      const compacted = compactZ(els)
      const fresh: SceneElement = {
        elementKind: candidate.kind,
        elementId: candidate.id,
        x: Math.round((canvasW - box.w) / 2),
        y: Math.round(canvasH * ADD_Y_FRACTION),
        z: compacted.length,
        scale,
        visible: true,
        w: box.w,
        h: box.h
      }
      return [...compacted, fresh]
    }, { label: 'add element', elementKey: elementKeyOf({ elementKind: candidate.kind, elementId: candidate.id }), coalesceKey: null })
    setSelectedElementKey(elementKeyOf({ elementKind: candidate.kind, elementId: candidate.id }))
  }

  const handleMoveLayer = (el: SceneElement, direction: -1 | 1): void => {
    const key = elementKeyOf(el)
    applyDraft((els) => {
      const ordered = [...els].sort((a, b) => a.z - b.z)
      const from = ordered.findIndex((e) => elementKeyOf(e) === key)
      const to = from + direction
      if (from === -1 || to < 0 || to >= ordered.length) return els
      const swapped = [...ordered]
      swapped[from] = ordered[to]
      swapped[to] = ordered[from]
      return swapped.map((e, i) => ({ ...e, z: i }))
    }, { label: direction === 1 ? 'bring forward' : 'send backward', elementKey: key, coalesceKey: null })
  }

  const handleToggleVisible = (el: SceneElement): void => {
    const key = elementKeyOf(el)
    applyDraft((els) => els.map((e) => elementKeyOf(e) === key ? { ...e, visible: !e.visible } : e),
      { label: el.visible ? 'hide element' : 'show element', elementKey: key, coalesceKey: null })
  }

  const handleRemoveElement = (el: SceneElement): void => {
    const key = elementKeyOf(el)
    setSelectedElementKey((prev) => prev === key ? null : prev)
    applyDraft((els) => els.filter((e) => elementKeyOf(e) !== key), { label: 'remove element', elementKey: key, coalesceKey: null })
  }

  const handleSaveDraft = async (): Promise<void> => {
    if (auth0Token === undefined || overlayCore === null || draft === null || !isDirty || elementsSaving || isEnded) return
    const sent = draft.elements
    const savedSceneId = draft.sceneId
    const elements = sent.map((el) => {
      if (el.elementKind === 'TICKER_BAR') {
        if (!streamTickerBars.some((t) => t.tickerBarId === el.elementId)) return el
        return { ...el, h: Math.round(tickerBarUnscaledHeightOf(streamTickerBars, el.elementId) * el.scale) }
      }
      if (!isCenterAnchoredKind(el.elementKind) && el.elementKind !== 'MERCH') return el
      const measured = contentHeights[elementKeyOf(el)]
      if (measured === undefined) return el
      return { ...el, h: Math.round(measured * el.scale) }
    })
    setElementsSaving(true)
    setQueueError(null)
    try {
      const { scene: updated } = await graphqlRequest<{ scene: OverlayScene }>(
        setSceneElementsMutation,
        {
          eventId: event.eventId,
          overlayId: overlayCore.overlayId,
          sceneId: draft.sceneId,
          elements: elements.map(toElementInput)
        },
        auth0Token
      )
      upsertScene(updated)
      setDraft((prev) => prev !== null && prev.elements === sent ? null : prev)
      setHistories((prev) => clearSceneDomain(prev, savedSceneId, 'layout'))
    } catch (err) {
      setQueueError(err instanceof Error ? err.message : 'Failed to save scene elements')
    } finally {
      setElementsSaving(false)
    }
  }

  const handleDiscardDraft = (): void => {
    if (draft === null) return
    if (isDirty && !globalThis.confirm('Discard unsaved changes?')) return
    const discardedSceneId = draft.sceneId
    setDraft(null)
    setHistories((prev) => clearSceneDomain(prev, discardedSceneId, 'layout'))
  }

  const previewPaneRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{
    key: string
    sceneId: string
    pointerId: number
    startClientX: number
    startClientY: number
    origX: number
    origY: number
    scale: number
    w: number
    h: number
    visualW: number
    visualH: number
    topOffset: number
    candidates: GuideCandidates
    curX: number
    curY: number
    moved: boolean
  } | null>(null)
  const [elementOverride, setElementOverride] = useState<ElementOverride | null>(null)
  const [activeGuides, setActiveGuides] = useState<ActiveGuides | null>(null)

  const pointerDownOnElementRef = useRef(false)
  const handleCanvasPointerDown = (): void => {
    if (pointerDownOnElementRef.current) {
      pointerDownOnElementRef.current = false
      return
    }
    setSelectedElementKey(null)
  }

  const guideCandidatesExcluding = (excludeKey: string): GuideCandidates => {
    if (overlayCore === null || selectedScene === null) return { vertical: [], horizontal: [] }
    const rects = draftElementsFor(selectedScene)
      .filter((el) => el.visible && el.elementKind !== 'CAMERA_CONTROL' &&
        elementKeyOf(el) !== excludeKey &&
        resolveElementName(event, streamChats, streamWalls, accountBoards, el) != null)
      .map(visualRectOf)
    return collectGuideCandidates(rects, overlayCore.width, overlayCore.height)
  }

  const handleElementPointerDown = (e: React.PointerEvent<HTMLDivElement>, el: SceneElement): void => {
    pointerDownOnElementRef.current = true
    const key = elementKeyOf(el)
    setSelectedElementKey(key)
    if (isEnded || selectedScene === null) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const adopted = withDerivedBox(el)
    const rect0 = visualRectOf(adopted)
    dragRef.current = {
      key,
      sceneId: selectedScene.sceneId,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      origX: el.x,
      origY: el.y,
      scale: adopted.scale,
      w: adopted.w ?? 0,
      h: adopted.h ?? 0,
      visualW: rect0.right - rect0.left,
      visualH: rect0.bottom - rect0.top,
      topOffset: rect0.top - el.y,
      candidates: guideCandidatesExcluding(key),
      curX: el.x,
      curY: el.y,
      moved: false
    }
  }

  const handleElementPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current
    if (drag === null || e.pointerId !== drag.pointerId) return
    const pane = previewPaneRef.current
    if (pane === null || overlayCore === null) return
    const rect = pane.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return
    if (!drag.moved) {
      const travel = Math.abs(e.clientX - drag.startClientX) + Math.abs(e.clientY - drag.startClientY)
      if (travel < DRAG_THRESHOLD_PX) return
      drag.moved = true
    }
    const { scale: paneScale } = paneCanvasMapping(rect, overlayCore.width, overlayCore.height)
    const rawX = drag.origX + (e.clientX - drag.startClientX) / paneScale
    const rawY = drag.origY + (e.clientY - drag.startClientY) / paneScale
    const threshold = SNAP_SCREEN_PX / paneScale
    const left = rawX
    const top = rawY + drag.topOffset
    const vSnap = snapPositions([left, left + drag.visualW / 2, left + drag.visualW], drag.candidates.vertical, threshold)
    const hSnap = snapPositions([top, top + drag.visualH / 2, top + drag.visualH], drag.candidates.horizontal, threshold)
    drag.curX = clampPos(rawX + (vSnap?.delta ?? 0))
    drag.curY = clampPos(rawY + (hSnap?.delta ?? 0))
    setElementOverride({ key: drag.key, x: drag.curX, y: drag.curY, scale: drag.scale, w: drag.w, h: drag.h })
    setActiveGuides(vSnap !== null || hSnap !== null
      ? { vertical: vSnap?.guide ?? null, horizontal: hSnap?.guide ?? null, thickness: Math.max(1, 1 / paneScale) }
      : null)
  }

  const handleElementPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current
    if (drag === null || e.pointerId !== drag.pointerId) return
    dragRef.current = null
    setActiveGuides(null)
    if (!drag.moved) return
    const baseline = scenes[drag.sceneId]
    if (baseline == null) {
      setElementOverride(null)
      return
    }
    commitDraft(
      drag.sceneId,
      draftElementsFor(baseline).map((el) => elementKeyOf(el) === drag.key ? { ...el, x: drag.curX, y: drag.curY } : el),
      { label: 'move', elementKey: drag.key, coalesceKey: null }
    )
    setElementOverride(null)
  }

  const handleElementPointerCancel = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current
    if (drag === null || e.pointerId !== drag.pointerId) return
    dragRef.current = null
    setElementOverride(null)
    setActiveGuides(null)
  }

  const resizeRef = useRef<ResizeGesture | null>(null)

  const handleResizePointerDown = (e: React.PointerEvent<HTMLDivElement>, el: SceneElement, handle: ResizeHandle): void => {
    e.stopPropagation()
    if (isEnded || selectedScene === null) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const key = elementKeyOf(el)
    const adopted = withDerivedBox(el)
    const box = resolveElementBox(adopted)
    const rect0 = visualRectOf(adopted)
    resizeRef.current = {
      key,
      sceneId: selectedScene.sceneId,
      pointerId: e.pointerId,
      handle,
      kind: el.elementKind,
      left: rect0.left,
      top: rect0.top,
      right: rect0.right,
      bottom: rect0.bottom,
      layoutWidth: box.layoutWidth,
      contentH: el.elementKind === 'TICKER_BAR'
        ? tickerBarUnscaledHeightOf(streamTickerBars, el.elementId)
        : contentHeights[key] ?? ELEMENT_BASE[el.elementKind].h,
      innerW: box.layoutWidth,
      innerH: layoutHeightOf(adopted, box, streamTickerBars),
      candidates: guideCandidatesExcluding(key),
      curX: el.x,
      curY: el.y,
      curW: rect0.right - rect0.left,
      curH: rect0.bottom - rect0.top,
      curScale: el.scale,
      changed: false,
      scaled: false
    }
  }

  const handleResizePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const r = resizeRef.current
    if (r === null || e.pointerId !== r.pointerId) return
    const pane = previewPaneRef.current
    if (pane === null || overlayCore === null) return
    const rect = pane.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return
    const { scale: paneScale, offsetX, offsetY } = paneCanvasMapping(rect, overlayCore.width, overlayCore.height)
    const px = (e.clientX - rect.left - offsetX) / paneScale
    const py = (e.clientY - rect.top - offsetY) / paneScale
    const threshold = SNAP_SCREEN_PX / paneScale
    const east = handleMovesEast(r.handle)
    const west = handleMovesWest(r.handle)
    const south = handleMovesSouth(r.handle)
    const north = handleMovesNorth(r.handle)
    let guideV: number | null = null
    let guideH: number | null = null
    if (isCornerHandle(r.handle) && (isCenterAnchoredKind(r.kind) || r.kind === 'CHAT' || r.kind === 'EMOTEWALL' || r.kind === 'MERCH' || r.kind === 'MERCH_TICKER' || r.kind === 'TICKER_BAR')) {
      const { refW, maxScale } = uniformScaleFrame(r)
      const s = clampRange(Math.abs(px - (east ? r.left : r.right)) / refW, SCALE_MIN, maxScale)
      applyUniformScale(r, s)
      r.scaled = true
    } else {
      const autoGrow = isCenterAnchoredKind(r.kind) || r.kind === 'MERCH' || r.kind === 'MERCH_TICKER' || r.kind === 'TICKER_BAR'
      const min = MIN_VISUAL[r.kind]
      let left = r.left
      let right = r.right
      let top = r.top
      let bottom = r.bottom
      if (east || west) {
        const snap = snapEdge(px, r.candidates.vertical, threshold)
        const edge = px + (snap?.delta ?? 0)
        const limited = east
          ? clampRange(edge, left + min.w, left + MAX_ELEMENT_SIZE)
          : clampRange(edge, Math.max(right - MAX_ELEMENT_SIZE, POS_MIN), right - min.w)
        if (east) right = limited
        else left = limited
        if (snap !== null && limited === edge) guideV = snap.guide
      }
      if ((south || north) && !autoGrow) {
        const snap = snapEdge(py, r.candidates.horizontal, threshold)
        const edge = py + (snap?.delta ?? 0)
        const limited = south
          ? clampRange(edge, top + min.h, top + MAX_ELEMENT_SIZE)
          : clampRange(edge, Math.max(bottom - MAX_ELEMENT_SIZE, POS_MIN), bottom - min.h)
        if (south) bottom = limited
        else top = limited
        if (snap !== null && limited === edge) guideH = snap.guide
      }
      r.curW = Math.round(right - left)
      r.curX = clampPos(left)
      if (!autoGrow) {
        r.curH = Math.round(bottom - top)
        r.curY = clampPos(top)
      }
    }
    r.changed = true
    setElementOverride({ key: r.key, x: r.curX, y: r.curY, scale: r.curScale, w: r.curW, h: r.curH })
    setActiveGuides(guideV !== null || guideH !== null
      ? { vertical: guideV, horizontal: guideH, thickness: Math.max(1, 1 / paneScale) }
      : null)
  }

  const handleResizePointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const r = resizeRef.current
    if (r === null || e.pointerId !== r.pointerId) return
    resizeRef.current = null
    setActiveGuides(null)
    if (!r.changed) {
      setElementOverride(null)
      return
    }
    const baseline = scenes[r.sceneId]
    if (baseline == null) {
      setElementOverride(null)
      return
    }
    if (r.scaled) {
      const { maxScale } = uniformScaleFrame(r)
      applyUniformScale(r, quantizeScale(r.curScale, maxScale))
    }
    commitDraft(
      r.sceneId,
      draftElementsFor(baseline).map((el) => elementKeyOf(el) === r.key
        ? { ...el, x: r.curX, y: r.curY, scale: r.curScale, w: r.curW, h: r.curH }
        : el),
      { label: 'resize', elementKey: r.key, coalesceKey: null }
    )
    setElementOverride(null)
  }

  const handleResizePointerCancel = (e: React.PointerEvent<HTMLDivElement>): void => {
    const r = resizeRef.current
    if (r === null || e.pointerId !== r.pointerId) return
    resizeRef.current = null
    setElementOverride(null)
    setActiveGuides(null)
  }

  useEffect(() => {
    if (isEnded || selectedScene === null || selectedElementKey === null) return
    const onKeyDown = (e: KeyboardEvent): void => {
      const dx = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
      const dy = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0
      if (dx === 0 && dy === 0) return
      const target = e.target
      if (target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' || target.isContentEditable)) return
      const base = draftElementsFor(selectedScene)
      if (!base.some((el) => elementKeyOf(el) === selectedElementKey)) return
      e.preventDefault()
      const step = e.shiftKey ? 10 : 1
      commitDraft(
        selectedScene.sceneId,
        base.map((el) => elementKeyOf(el) === selectedElementKey
          ? { ...el, x: clampPos(el.x + dx * step), y: clampPos(el.y + dy * step) }
          : el),
        { label: 'nudge', elementKey: selectedElementKey, coalesceKey: `nudge:${selectedElementKey}` }
      )
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isEnded, selectedScene, selectedElementKey, draftElementsFor, commitDraft])

  const [resyncBusy, setResyncBusy] = useState(false)
  const handleResync = async (): Promise<void> => {
    if (resyncBusy || isEnded || auth0Token === undefined) return
    setResyncBusy(true)
    try {
      await graphqlRequest<{ result: { republished: number } }>(
        republishEventStateMutation, { eventId: event.eventId }, auth0Token
      )
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Failed to resync overlays')
    } finally {
      setResyncBusy(false)
    }
  }

  const handleTakeLive = async (transition: 'CUT' | 'FADE'): Promise<void> => {
    if (auth0Token === undefined || overlayCore === null || selectedScene === null || liveBusy !== null || isEnded) return
    setLiveBusy(transition === 'FADE' ? 'fade' : 'take')
    setLiveError(null)
    try {
      const { overlay } = await graphqlRequest<{ overlay: OverlayCore }>(
        takeSceneLiveMutation,
        { eventId: event.eventId, overlayId: overlayCore.overlayId, sceneId: selectedScene.sceneId, transition },
        auth0Token
      )
      applyOverlayCore(overlay, transition)
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Failed to take scene live')
    } finally {
      setLiveBusy(null)
    }
  }

  const handleClearLive = async (): Promise<void> => {
    if (auth0Token === undefined || overlayCore === null || liveBusy !== null || isEnded) return
    setLiveBusy('clear')
    setLiveError(null)
    try {
      const { overlay } = await graphqlRequest<{ overlay: OverlayCore }>(
        clearOverlayLiveMutation,
        { eventId: event.eventId, overlayId: overlayCore.overlayId, transition: 'CUT' },
        auth0Token
      )
      applyOverlayCore(overlay, 'CUT')
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Failed to hide the overlay')
    } finally {
      setLiveBusy(null)
    }
  }

  const selectedPoll = selectedElement !== null && selectedElement.elementKind === 'POLL'
    ? event.polls.find((p) => p.pollId === selectedElement.elementId) ?? null
    : null
  const selectedTug = selectedElement !== null && selectedElement.elementKind === 'TUG'
    ? event.tugs.find((t) => t.tugId === selectedElement.elementId) ?? null
    : null
  const selectedBoard = selectedElement !== null && selectedElement.elementKind === 'BOARD'
    ? event.scoreboards.find((s) => s.boardId === selectedElement.elementId) ?? null
    : null
  const selectedMerchBoard = selectedElement !== null &&
    (selectedElement.elementKind === 'MERCH' || selectedElement.elementKind === 'MERCH_TICKER')
    ? accountBoards.find((b) => b.merchOverlayId === selectedElement.elementId) ?? null
    : null
  const selectedCamCtl = selectedElement !== null && selectedElement.elementKind === 'CAMERA_CONTROL'
    ? event.cameraControls.find((c) => c.camControlId === selectedElement.elementId) ?? null
    : null
  const selectedGiveaway = selectedElement !== null && selectedElement.elementKind === 'GIVEAWAY'
    ? event.giveaways.find((g) => g.giveawayId === selectedElement.elementId) ?? null
    : null
  const selectedTrivia = selectedElement !== null && selectedElement.elementKind === 'TRIVIA'
    ? event.trivias.find((t) => t.triviaId === selectedElement.elementId) ?? null
    : null
  const selectedTriviaPhase = selectedTrivia != null ? triviaPhaseOf(selectedTrivia, triviaNowMs) : null
  const triviaRevealNowOpen = selectedTrivia != null &&
    triviaRevealNowFor === selectedTrivia.triviaId &&
    selectedTriviaPhase === 'counting'
  useEffect(() => {
    if (triviaRevealNowFor != null && !triviaRevealNowOpen) setTriviaRevealNowFor(null)
  }, [triviaRevealNowFor, triviaRevealNowOpen])
  const ytWarnVisible = ytWarn != null && (
    (ytWarn.kind === 'POLL' && selectedPoll != null && selectedPoll.pollId === ytWarn.id && selectedPoll.status === 'ACTIVE') ||
    (ytWarn.kind === 'TUG' && selectedTug != null && selectedTug.tugId === ytWarn.id && selectedTug.status === 'ACTIVE') ||
    (ytWarn.kind === 'TRIVIA' && selectedTrivia != null && selectedTrivia.triviaId === ytWarn.id && selectedTrivia.status === 'ACTIVE')
  )

  const handlePollStart = async (): Promise<void> => {
    if (auth0Token === undefined || selectedPoll === null || controlsBusy !== null || isEnded) return
    const pollId = selectedPoll.pollId
    const countsYouTube = selectedPoll.youtubeVotesEnabled === true
    setControlsBusy('start')
    setControlsError(null)
    try {
      const { poll: updated } = await graphqlRequest<{ poll: Pick<PollSummary, 'pollId' | 'status' | 'startedAt'> }>(
        startPollMutation, { pollId }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            polls: prev.polls.map((p) => p.pollId === pollId
              ? { ...p, status: updated.status, startedAt: updated.startedAt }
              : p)
          }))
      if (countsYouTube && updated.status === 'ACTIVE') armYouTubeWarning('POLL', pollId)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to start poll')
    } finally {
      setControlsBusy(null)
    }
  }

  const handlePollStop = async (): Promise<void> => {
    if (auth0Token === undefined || selectedPoll === null || controlsBusy !== null || isEnded) return
    const pollId = selectedPoll.pollId
    setControlsBusy('stop')
    setControlsError(null)
    try {
      const { poll: updated } = await graphqlRequest<{ poll: Pick<PollSummary, 'pollId' | 'status' | 'stoppedAt' | 'graceEndsAt'> }>(
        stopPollMutation, { pollId }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            polls: prev.polls.map((p) => p.pollId === pollId
              ? { ...p, status: updated.status, stoppedAt: updated.stoppedAt, graceEndsAt: updated.graceEndsAt ?? null }
              : p)
          }))
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to stop poll')
    } finally {
      setControlsBusy(null)
    }
  }

  const handlePollReset = async (): Promise<void> => {
    if (auth0Token === undefined || selectedPoll === null || controlsBusy !== null || isEnded) return
    const pollId = selectedPoll.pollId
    const ok = globalThis.confirm(pollResetConfirmCopy(selectedPoll.name, pollResetIsLive(selectedPoll, Date.now())))
    if (!ok) return
    setControlsBusy('reset')
    setControlsError(null)
    try {
      const { poll: updated } = await graphqlRequest<{ poll: PollResetResponse }>(
        resetPollCountsMutation, { pollId, clearDedupe: true }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            polls: prev.polls.map((p) => p.pollId === pollId ? foldPollReset(p, updated) : p)
          }))
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to reset counts')
    } finally {
      setControlsBusy(null)
    }
  }

  const mergePollRow = (updated: Partial<PollSummary> & { pollId: string }): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          polls: prev.polls.map((p) => p.pollId === updated.pollId ? { ...p, ...updated } : p)
        }))
  }

  const foldTriviaRow = (updated: TriviaSummary, clearStamps = false): void => {
    const row = clearStamps ? { ...updated, closedAt: null, graceEndsAt: null } : updated
    setEvent((prev) => {
      if (prev === null) return prev
      const idx = prev.trivias.findIndex((t) => t.triviaId === row.triviaId)
      if (idx === -1) return { ...prev, trivias: [...prev.trivias, row] }
      if (prev.trivias[idx].version > row.version) return prev
      const next = prev.trivias.slice()
      next[idx] = row
      return { ...prev, trivias: next }
    })
  }

  const handleTriviaStart = async (): Promise<void> => {
    if (auth0Token === undefined || selectedTrivia === null || controlsBusy !== null || isEnded) return
    const countsYouTube = selectedTrivia.youtubeVotesEnabled === true
    setControlsBusy('start')
    setControlsError(null)
    try {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        startTriviaMutation, { triviaId: selectedTrivia.triviaId }, auth0Token
      )
      foldTriviaRow(updated, true)
      if (countsYouTube && updated.status === 'ACTIVE') armYouTubeWarning('TRIVIA', updated.triviaId)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to start the question')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleTriviaCancel = async (): Promise<void> => {
    if (auth0Token === undefined || selectedTrivia === null || controlsBusy !== null || isEnded) return
    setControlsBusy('stop')
    setControlsError(null)
    try {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        stopTriviaMutation, { triviaId: selectedTrivia.triviaId }, auth0Token
      )
      foldTriviaRow(updated, true)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to cancel the question')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleTriviaReset = async (): Promise<void> => {
    if (auth0Token === undefined || selectedTrivia === null || controlsBusy !== null || isEnded) return
    const ok = globalThis.confirm(triviaResetConfirmCopy(selectedTrivia.name, selectedTrivia.revealedAt != null))
    if (!ok) return
    setControlsBusy('reset')
    setControlsError(null)
    try {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        resetTriviaMutation, { triviaId: selectedTrivia.triviaId, clearDedupe: true }, auth0Token
      )
      foldTriviaRow(updated)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to reset the question')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleTriviaClose = async (): Promise<void> => {
    if (auth0Token === undefined || selectedTrivia === null || controlsBusy !== null || isEnded) return
    setControlsBusy('close')
    setControlsError(null)
    try {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        closeTriviaMutation, { triviaId: selectedTrivia.triviaId }, auth0Token
      )
      foldTriviaRow(updated)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to close the question')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleTriviaReveal = async (force = false): Promise<void> => {
    if (auth0Token === undefined || selectedTrivia === null || controlsBusy !== null) return
    setControlsBusy('reveal')
    setControlsError(null)
    try {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        revealTriviaMutation,
        force ? { triviaId: selectedTrivia.triviaId, force: true } : { triviaId: selectedTrivia.triviaId },
        auth0Token
      )
      foldTriviaRow(updated)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to reveal the answer')
    } finally {
      setControlsBusy(null)
    }
  }

  const confirmTriviaRevealNow = (): void => {
    setTriviaRevealNowFor(null)
    if (selectedTrivia == null) return
    void handleTriviaReveal(triviaYouTubeCounting(selectedTrivia, Date.now()))
  }

  const boundaryRef = useRef<{
    pollId: string
    pointerId: number
    boundary: number
    layoutWidth: number
    glyphScale: number
    startClientX: number
    startWeights: number[]
    curWeights: number[]
    baseline: number[] | null
    prior: { weights: number[] | null } | null
    moved: boolean
  } | null>(null)

  const commitPollWeights = async (pollId: string, weights: number[] | null): Promise<void> => {
    if (auth0Token === undefined) return
    setWeightsBusy(true)
    setWeightsError(null)
    try {
      const { poll: updated } = await graphqlRequest<{ poll: PollSummary }>(
        updatePollWeightsMutation, { pollId, tickerBlockWeights: weights }, auth0Token
      )
      mergePollRow(updated)
      setPollWeightsPreview((prev) => prev !== null && prev.pollId === pollId ? null : prev)
      setHistories((prev) => clearDomainEverywhere(prev, 'weights', pollId))
    } catch (err) {
      setPollWeightsPreview((prev) => prev !== null && prev.pollId === pollId ? null : prev)
      setHistories((prev) => clearDomainEverywhere(prev, 'weights', pollId))
      setWeightsError(err instanceof Error ? err.message : 'Failed to save the block widths')
    } finally {
      setWeightsBusy(false)
    }
  }

  const confirmReplaceForeignWeightsPreview = (pollId: string): boolean => {
    if (pollWeightsPreview === null || pollWeightsPreview.pollId === pollId) return true
    if (!weightsDirty) return true
    return globalThis.confirm('Another ticker has unsaved block-width changes. Discard them and edit this one?')
  }

  const replaceForeignWeightsPreview = (pollId: string): boolean => {
    const discardedPollId = pollWeightsPreview != null && pollWeightsPreview.pollId !== pollId && weightsDirty ? pollWeightsPreview.pollId : null
    if (!confirmReplaceForeignWeightsPreview(pollId)) return false
    if (discardedPollId != null) {
      setPollWeightsPreview((prev) => prev != null && prev.pollId === discardedPollId ? null : prev)
      setHistories((prev) => clearDomainEverywhere(prev, 'weights', discardedPollId))
    }
    return true
  }

  const storedWeightsOf = (pollId: string): number[] | null =>
    event.polls.find((p) => p.pollId === pollId)?.tickerBlockWeights ?? null
  const recordWeightsEdit = (pollId: string, before: WeightsSlot | null, after: WeightsSlot | null, label: string, coalesceKey: string | null): void => {
    if (selectedScene == null) return
    const sceneId = selectedScene.sceneId
    const stored = storedWeightsOf(pollId)
    const entry: WeightsEntry = {
      domain: 'weights',
      pollId,
      before: { slot: normalizeWeightsSlot(before), stored },
      after: { slot: normalizeWeightsSlot(after), stored },
      label,
      elementKey: elementKeyOf({ elementKind: 'POLL', elementId: pollId }),
      coalesceKey,
      at: Date.now()
    }
    setHistories((prev) => recordSceneEdit(prev, sceneId, entry))
  }

  const handleBoundaryPointerDown = (e: React.PointerEvent<HTMLDivElement>, el: SceneElement, boundary: number): void => {
    e.stopPropagation()
    if (isEnded || weightsBusy) return
    const poll = el.elementKind === 'POLL'
      ? event.polls.find((p) => p.pollId === el.elementId) ?? null
      : null
    if (poll === null || poll.template !== 'TICKER') return
    if (poll.status === 'ACTIVE') return
    if (!replaceForeignWeightsPreview(poll.pollId)) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const box = resolveElementBox(withDerivedBox(el))
    const prior = pollWeightsPreview !== null && pollWeightsPreview.pollId === poll.pollId
      ? pollWeightsPreview
      : null
    const start = prior !== null
      ? prior.weights ?? adoptTickerWeights(null, poll.options.length, box.layoutWidth)
      : adoptTickerWeights(poll.tickerBlockWeights ?? null, poll.options.length, box.layoutWidth)
    boundaryRef.current = {
      pollId: poll.pollId,
      pointerId: e.pointerId,
      boundary,
      layoutWidth: box.layoutWidth,
      glyphScale: box.glyphScale,
      startClientX: e.clientX,
      startWeights: start,
      curWeights: start,
      baseline: prior !== null ? prior.baseline : start,
      prior: prior !== null ? { weights: prior.weights } : null,
      moved: false
    }
  }

  const handleBoundaryPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const g = boundaryRef.current
    if (g === null || e.pointerId !== g.pointerId) return
    const pane = previewPaneRef.current
    if (pane === null || overlayCore === null) return
    const rect = pane.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return
    const { scale: paneScale } = paneCanvasMapping(rect, overlayCore.width, overlayCore.height)
    const dxLayout = (e.clientX - g.startClientX) / paneScale / g.glyphScale
    if (!g.moved && dxLayout === 0) return
    g.moved = true
    g.curWeights = shiftTickerBoundary(g.startWeights, g.boundary, dxLayout / g.layoutWidth, g.layoutWidth)
    setPollWeightsPreview({ pollId: g.pollId, weights: g.curWeights, baseline: g.baseline })
  }

  const handleBoundaryPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const g = boundaryRef.current
    if (g === null || e.pointerId !== g.pointerId) return
    boundaryRef.current = null
    setPollWeightsPreview((prev) => {
      if (prev === null || prev.pollId !== g.pollId) return prev
      return tickerWeightsEq(prev.weights, prev.baseline) ? null : prev
    })
    if (g.moved) {
      recordWeightsEdit(
        g.pollId,
        g.prior != null ? { pollId: g.pollId, weights: g.prior.weights, baseline: g.baseline } : null,
        { pollId: g.pollId, weights: g.curWeights, baseline: g.baseline },
        'divider drag',
        null
      )
    }
  }

  const handleBoundaryPointerCancel = (e: React.PointerEvent<HTMLDivElement>): void => {
    const g = boundaryRef.current
    if (g === null || e.pointerId !== g.pointerId) return
    boundaryRef.current = null
    setPollWeightsPreview((prev) => {
      if (prev === null || prev.pollId !== g.pollId) return prev
      if (g.prior === null) return null
      return tickerWeightsEq(g.prior.weights, g.baseline)
        ? null
        : { pollId: g.pollId, weights: g.prior.weights, baseline: g.baseline }
    })
  }

  const handleWeightsNudge = (boundary: number, direction: -1 | 1): void => {
    if (selectedPoll === null || selectedElement === null || isEnded || weightsBusy) return
    if (selectedPoll.status === 'ACTIVE' || selectedPoll.template !== 'TICKER') return
    const pollId = selectedPoll.pollId
    if (!replaceForeignWeightsPreview(pollId)) return
    const box = resolveElementBox(withDerivedBox(selectedElement))
    const prior = pollWeightsPreview !== null && pollWeightsPreview.pollId === pollId
      ? pollWeightsPreview
      : null
    const current = prior !== null
      ? prior.weights ?? adoptTickerWeights(null, selectedPoll.options.length, box.layoutWidth)
      : adoptTickerWeights(selectedPoll.tickerBlockWeights ?? null, selectedPoll.options.length, box.layoutWidth)
    const baseline = prior !== null ? prior.baseline : current
    const next = shiftTickerBoundary(current, boundary, direction * POLL.ticker.blocks.nudgeFraction, box.layoutWidth)
    const nextSlot = tickerWeightsEq(next, baseline) ? null : { pollId, weights: next, baseline }
    setPollWeightsPreview(nextSlot)
    recordWeightsEdit(pollId, prior, nextSlot, 'divider nudge', `divider:${pollId}:${boundary}`)
  }

  const handleWeightsReset = (): void => {
    if (selectedPoll === null || isEnded || weightsBusy) return
    if (selectedPoll.status === 'ACTIVE') return
    const pollId = selectedPoll.pollId
    if (!replaceForeignWeightsPreview(pollId)) return
    const stored = selectedPoll.tickerBlockWeights ?? null
    const prior = pollWeightsPreview !== null && pollWeightsPreview.pollId === pollId
      ? pollWeightsPreview
      : null
    const effective = prior !== null ? prior.weights : stored
    if (effective === null) return
    if (stored === null) {
      setPollWeightsPreview((prev) => prev !== null && prev.pollId === pollId ? null : prev)
      recordWeightsEdit(pollId, prior, null, 'reset to equal', null)
      return
    }
    const resetSlot = { pollId, weights: null, baseline: prior != null ? prior.baseline : stored }
    setPollWeightsPreview(resetSlot)
    recordWeightsEdit(pollId, prior, resetSlot, 'reset to equal', null)
  }

  const handleWeightsSave = (): void => {
    if (selectedPoll === null || isEnded || weightsBusy) return
    if (selectedPoll.status === 'ACTIVE') return
    const prior = pollWeightsPreview !== null && pollWeightsPreview.pollId === selectedPoll.pollId
      ? pollWeightsPreview
      : null
    if (prior === null || tickerWeightsEq(prior.weights, prior.baseline)) return
    void commitPollWeights(prior.pollId, prior.weights)
  }

  const handleWeightsDiscard = (): void => {
    if (selectedPoll === null || weightsBusy) return
    const prior = pollWeightsPreview !== null && pollWeightsPreview.pollId === selectedPoll.pollId
      ? pollWeightsPreview
      : null
    if (prior === null) return
    const dirty = !tickerWeightsEq(prior.weights, prior.baseline)
    if (dirty && !globalThis.confirm('Discard unsaved block-width changes?')) return
    setPollWeightsPreview((prev) => prev !== null && prev.pollId === prior.pollId ? null : prev)
    setHistories((prev) => clearDomainEverywhere(prev, 'weights', prior.pollId))
  }

  const selectedWeightsPreview = selectedPoll !== null && pollWeightsPreview !== null && pollWeightsPreview.pollId === selectedPoll.pollId
    ? pollWeightsPreview
    : null
  const selectedWeightsDirty = selectedWeightsPreview !== null && !tickerWeightsEq(selectedWeightsPreview.weights, selectedWeightsPreview.baseline)
  const selectedEffectiveWeights = selectedWeightsPreview !== null
    ? selectedWeightsPreview.weights
    : selectedPoll?.tickerBlockWeights ?? null

  useEffect(() => {
    const onPointerEnd = (e: PointerEvent): void => {
      const drag = dragRef.current
      const resize = resizeRef.current
      const orphanDrag = drag != null && drag.pointerId === e.pointerId
      const orphanResize = resize != null && resize.pointerId === e.pointerId
      if (orphanDrag) dragRef.current = null
      if (orphanResize) resizeRef.current = null
      if (orphanDrag || orphanResize) {
        setElementOverride(null)
        setActiveGuides(null)
      }
      const g = boundaryRef.current
      if (g == null || g.pointerId !== e.pointerId) return
      boundaryRef.current = null
      setPollWeightsPreview((prev) => {
        if (prev == null || prev.pollId !== g.pollId) return prev
        if (g.prior == null) return null
        return tickerWeightsEq(g.prior.weights, g.baseline)
          ? null
          : { pollId: g.pollId, weights: g.prior.weights, baseline: g.baseline }
      })
    }
    document.addEventListener('pointerup', onPointerEnd)
    document.addEventListener('pointercancel', onPointerEnd)
    return () => {
      document.removeEventListener('pointerup', onPointerEnd)
      document.removeEventListener('pointercancel', onPointerEnd)
    }
  }, [])

  const handleHistoryStep = (direction: HistoryDirection): void => {
    if (isEnded || selectedScene == null || elementsSaving || weightsBusy) return
    if (dragRef.current != null || resizeRef.current != null || boundaryRef.current != null) return
    const scene = selectedScene
    const sceneId = scene.sceneId
    const entry = topEntryOf(histories[sceneId], direction)
    if (entry == null) return
    const reselect = (elementKey: string | null, els: SceneElement[]): void => {
      const has = (key: string): boolean => els.some((el) => elementKeyOf(el) === key)
      if (elementKey != null && has(elementKey)) setSelectedElementKey(elementKey)
      else setSelectedElementKey((prev) => prev != null && !has(prev) ? null : prev)
    }
    if (entry.domain === 'layout') {
      if (!layoutEntryApplies(entry, direction, draftElementsFor(scene))) {
        setHistories((prev) => clearSceneDomain(prev, sceneId, 'layout'))
        return
      }
      const restored = direction === 'undo' ? entry.before : entry.after
      if (elementsEqual(restored, scene.elements.map(withDerivedBox))) {
        setDraft((prev) => prev != null && prev.sceneId === sceneId ? null : prev)
      } else {
        const replaced = draft != null && draft.sceneId !== sceneId && isDirty ? draft.sceneId : null
        if (replaced != null) setHistories((prev) => clearSceneDomain(prev, replaced, 'layout'))
        setDraft({ sceneId, elements: restored })
        setSelectedSceneId((prev) => prev ?? sceneId)
      }
      reselect(entry.elementKey, restored)
    } else {
      const poll = event.polls.find((p) => p.pollId === entry.pollId)
      const current: WeightsSnapshot = { slot: normalizeWeightsSlot(pollWeightsPreview), stored: storedWeightsOf(entry.pollId) }
      if (poll == null || poll.status === 'ACTIVE' || poll.template !== 'TICKER' || !weightsEntryApplies(entry, direction, current)) {
        setHistories((prev) => clearSceneDomain(prev, sceneId, 'weights', entry.pollId))
        return
      }
      setPollWeightsPreview((direction === 'undo' ? entry.before : entry.after).slot)
      reselect(entry.elementKey, draftElementsFor(scene))
    }
    setHistories((prev) => stepSceneHistory(prev, sceneId, direction, entry))
  }

  const historyStepRef = useRef(handleHistoryStep)
  historyStepRef.current = handleHistoryStep
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      const action = historyShortcutOf(e)
      if (action == null || isTypingTarget(e.target)) return
      e.preventDefault()
      historyStepRef.current(action)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const handleTugStart = async (): Promise<void> => {
    if (auth0Token === undefined || selectedTug === null || controlsBusy !== null || isEnded) return
    const tugId = selectedTug.tugId
    const countsYouTube = selectedTug.youtubeVotesEnabled === true
    setControlsBusy('start')
    setControlsError(null)
    try {
      const { tug: updated } = await graphqlRequest<{ tug: Pick<TugSummary, 'tugId' | 'status' | 'startedAt'> }>(
        startTugOfWarMutation, { tugId }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            tugs: prev.tugs.map((t) => t.tugId === tugId ? { ...t, status: updated.status, startedAt: updated.startedAt } : t)
          }))
      if (countsYouTube && updated.status === 'ACTIVE') armYouTubeWarning('TUG', tugId)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to start tug of war')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleTugStop = async (): Promise<void> => {
    if (auth0Token === undefined || selectedTug === null || controlsBusy !== null || isEnded) return
    const tugId = selectedTug.tugId
    setControlsBusy('stop')
    setControlsError(null)
    try {
      const { tug: updated } = await graphqlRequest<{ tug: Pick<TugSummary, 'tugId' | 'status' | 'stoppedAt' | 'graceEndsAt'> }>(
        stopTugOfWarMutation, { tugId }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            tugs: prev.tugs.map((t) => t.tugId === tugId
              ? { ...t, status: updated.status, stoppedAt: updated.stoppedAt, graceEndsAt: updated.graceEndsAt ?? null }
              : t)
          }))
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to stop tug of war')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleTugReset = async (): Promise<void> => {
    if (auth0Token === undefined || selectedTug === null || controlsBusy !== null || isEnded) return
    const tugId = selectedTug.tugId
    const ok = globalThis.confirm(tugResetConfirmCopy(selectedTug.name, tugResetIsLive(selectedTug, Date.now())))
    if (!ok) return
    setControlsBusy('reset')
    setControlsError(null)
    try {
      const { tug: updated } = await graphqlRequest<{ tug: TugResetResponse }>(
        resetTugOfWarCountsMutation, { tugId, clearDedupe: true }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            tugs: prev.tugs.map((t) => t.tugId === tugId ? foldTugReset(t, updated) : t)
          }))
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to reset counts')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleCamCtlStart = async (): Promise<void> => {
    if (auth0Token === undefined || selectedCamCtl === null || controlsBusy !== null || isEnded) return
    const camControlId = selectedCamCtl.camControlId
    setControlsBusy('start')
    setControlsError(null)
    try {
      const { camControl: updated } = await graphqlRequest<{ camControl: Pick<CameraControlSummary, 'camControlId' | 'status' | 'startedAt' | 'stoppedAt' | 'version' | 'currentCommandIndex' | 'streak'> }>(
        startCameraControlMutation, { camControlId }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            cameraControls: prev.cameraControls.map((c) => c.camControlId === camControlId
              ? { ...c, status: updated.status, startedAt: updated.startedAt, stoppedAt: updated.stoppedAt, currentCommandIndex: updated.currentCommandIndex, streak: updated.streak, version: updated.version }
              : c)
          }))
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to start camera control')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleCamCtlStop = async (): Promise<void> => {
    if (auth0Token === undefined || selectedCamCtl === null || controlsBusy !== null || isEnded) return
    const camControlId = selectedCamCtl.camControlId
    setControlsBusy('stop')
    setControlsError(null)
    try {
      const { camControl: updated } = await graphqlRequest<{ camControl: Pick<CameraControlSummary, 'camControlId' | 'status' | 'startedAt' | 'stoppedAt' | 'version' | 'currentCommandIndex' | 'streak'> }>(
        stopCameraControlMutation, { camControlId }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            cameraControls: prev.cameraControls.map((c) => c.camControlId === camControlId
              ? { ...c, status: updated.status, startedAt: updated.startedAt, stoppedAt: updated.stoppedAt, currentCommandIndex: updated.currentCommandIndex, streak: updated.streak, version: updated.version }
              : c)
          }))
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to stop camera control')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleCamCtlReset = async (): Promise<void> => {
    if (auth0Token === undefined || selectedCamCtl === null || controlsBusy !== null || isEnded) return
    const camControlId = selectedCamCtl.camControlId
    const ok = globalThis.confirm(
      `Reset "${selectedCamCtl.name}"?\n\n` +
      'This clears every command counter, DISCARDS any queued wins still waiting out the cooldown, and resets the win streak. The cam-op page returns to its waiting state.\n\n' +
      'Clear counters, queue and streak?'
    )
    if (!ok) return
    setControlsBusy('reset')
    setControlsError(null)
    try {
      const { camControl: updated } = await graphqlRequest<{ camControl: Pick<CameraControlSummary, 'camControlId' | 'commands' | 'currentCommandIndex' | 'streak' | 'version'> }>(
        resetCameraControlCountsMutation, { camControlId }, auth0Token
      )
      setEvent((prev) => prev === null
        ? prev
        : ({
            ...prev,
            cameraControls: prev.cameraControls.map((c) => c.camControlId === camControlId
              ? { ...c, commands: updated.commands, currentCommandIndex: updated.currentCommandIndex, streak: updated.streak, version: updated.version }
              : c)
          }))
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to reset counts')
    } finally {
      setControlsBusy(null)
    }
  }

  type GiveawayLiveResponse = Pick<GiveawaySummary, 'giveawayId' | 'status' | 'entrantCount' | 'hasDraw' | 'startedAt' | 'closedAt' | 'publishedAt' | 'version' | 'winners'> & { draw?: GiveawaySummary['draw'] }
  const mergeGiveaway = (updated: GiveawayLiveResponse, extra: Partial<GiveawaySummary> = {}): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          giveaways: prev.giveaways.map((g) => g.giveawayId === updated.giveawayId
            ? { ...g, ...updated, ...extra }
            : g)
        }))
  }

  const handleGiveawayStart = async (): Promise<void> => {
    if (auth0Token === undefined || selectedGiveaway === null || controlsBusy !== null || isEnded) return
    const giveawayId = selectedGiveaway.giveawayId
    setControlsBusy('start')
    setControlsError(null)
    try {
      const { giveaway: updated } = await graphqlRequest<{ giveaway: GiveawayLiveResponse }>(
        startGiveawayMutation, { giveawayId }, auth0Token
      )
      mergeGiveaway(updated)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to open the giveaway')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleGiveawayStop = async (): Promise<void> => {
    if (auth0Token === undefined || selectedGiveaway === null || controlsBusy !== null) return
    const giveawayId = selectedGiveaway.giveawayId
    setControlsBusy('stop')
    setControlsError(null)
    try {
      const { giveaway: updated } = await graphqlRequest<{ giveaway: GiveawayLiveResponse }>(
        stopGiveawayMutation, { giveawayId }, auth0Token
      )
      mergeGiveaway(updated)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to close the giveaway')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleGiveawayCalculate = async (): Promise<void> => {
    if (auth0Token === undefined || selectedGiveaway === null || controlsBusy !== null) return
    const giveawayId = selectedGiveaway.giveawayId
    setControlsBusy('calculate')
    setControlsError(null)
    try {
      const { giveaway: updated } = await graphqlRequest<{ giveaway: GiveawayLiveResponse }>(
        calculateGiveawayWinnersMutation, { giveawayId }, auth0Token
      )
      mergeGiveaway(updated)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to calculate winners')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleGiveawayPublish = async (): Promise<void> => {
    if (auth0Token === undefined || selectedGiveaway === null || controlsBusy !== null) return
    const giveawayId = selectedGiveaway.giveawayId
    setControlsBusy('publish')
    setControlsError(null)
    try {
      const { giveaway: updated } = await graphqlRequest<{ giveaway: GiveawayLiveResponse }>(
        publishGiveawayWinnersMutation, { giveawayId }, auth0Token
      )
      mergeGiveaway(updated)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to publish winners')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleGiveawayReset = async (): Promise<void> => {
    if (auth0Token === undefined || selectedGiveaway === null || controlsBusy !== null || isEnded) return
    const giveawayId = selectedGiveaway.giveawayId
    const ok = globalThis.confirm(
      `Reset "${selectedGiveaway.name}"?\n\n` +
      `TESTING ONLY. This permanently DELETES all ${selectedGiveaway.entrantCount.toLocaleString()} entrant records${selectedGiveaway.hasDraw ? ' AND the completed draw record (winners + alternates)' : ''}, sets the count to zero and returns the giveaway to Upcoming. It cannot be undone, and the reset is recorded on the giveaway's audit trail.\n\n` +
      'Never reset a real promotion — create a new giveaway for the next prize instead.\n\n' +
      'Delete every entrant and the draw, and reset?'
    )
    if (!ok) return
    setControlsBusy('reset')
    setControlsError(null)
    try {
      const { giveaway: updated } = await graphqlRequest<{ giveaway: GiveawayLiveResponse }>(
        resetGiveawayMutation, { giveawayId }, auth0Token
      )
      mergeGiveaway(updated, { draw: null })
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to reset the giveaway')
    } finally {
      setControlsBusy(null)
    }
  }

  const handleGiveawayDisqualify = async (w: GiveawayWinner): Promise<void> => {
    if (auth0Token === undefined || selectedGiveaway === null || controlsBusy !== null) return
    if (selectedGiveaway.status === 'ENDED') return
    if (w.twitchUserId == null) return
    const giveawayId = selectedGiveaway.giveawayId
    const reason = promptDisqualifyReason(w)
    if (reason === null) return
    if (reason.trim().length === 0) {
      setControlsError('A reason is required to disqualify a winner.')
      return
    }
    setControlsBusy(`disqualify:${w.twitchUserId}`)
    setControlsError(null)
    try {
      const { giveaway: updated } = await graphqlRequest<{ giveaway: GiveawayLiveResponse }>(
        disqualifyGiveawayWinnerMutation,
        { giveawayId, twitchUserId: w.twitchUserId, reason: reason.trim() },
        auth0Token
      )
      mergeGiveaway(updated)
    } catch (err) {
      setControlsError(err instanceof Error ? err.message : 'Failed to disqualify the winner')
    } finally {
      setControlsBusy(null)
    }
  }

  if (overlayCore === null) {
    return (
      <div className='mt-2 px-4 pb-4'>
        {loadError !== null
          ? <ErrorInline>{loadError}</ErrorInline>
          : (
            <div className='flex items-center justify-center gap-2 py-16 text-sm text-dm-text-secondary'>
              <LoadingSpinner />
              Loading overlay…
            </div>
            )}
      </div>
    )
  }

  const selectedIsLive = liveSnapshot !== null && selectedScene !== null &&
    overlayCore.liveSceneId === selectedScene.sceneId
  const liveSourceScene = overlayCore.liveSceneId !== null ? scenes[overlayCore.liveSceneId] : undefined
  const liveSourceDrift = liveSnapshot !== null && liveSourceScene !== undefined &&
    liveSourceScene.version > liveSnapshot.sourceVersion
  const selectedHasDrift = selectedIsLive && liveSourceDrift
  const selectedIsDirty = isDirty && draft !== null && selectedScene !== null &&
    draft.sceneId === selectedScene.sceneId
  const sceneQueueRows = workingElements !== null
    ? [...workingElements].sort((a, b) => a.z - b.z)
    : []
  const selectedLayerIndex = selectedElement !== null
    ? sceneQueueRows.findIndex((el) => elementKeyOf(el) === elementKeyOf(selectedElement))
    : -1
  const historyLocked = isEnded || selectedScene == null || elementsSaving || weightsBusy
  const sceneHistory = selectedScene != null ? histories[selectedScene.sceneId] : undefined
  const undoEntry = historyLocked ? null : topEntryOf(sceneHistory, 'undo')
  const redoEntry = historyLocked ? null : topEntryOf(sceneHistory, 'redo')
  const undoTitle = `Undo${undoEntry != null ? ` ${undoEntry.label}` : ''} (${undoShortcutHint(macPlatform)})`
  const redoTitle = `Redo${redoEntry != null ? ` ${redoEntry.label}` : ''} (${redoShortcutHint(macPlatform)})`

  const isPortrait = overlayCore.height > overlayCore.width
  const portraitPaneHeight = 'min(60vh, 46rem)'
  const paneBoxStyle: CSSProperties = isPortrait
    ? {
        height: portraitPaneHeight,
        width: `calc(${portraitPaneHeight} * ${(overlayCore.width / overlayCore.height).toFixed(6)})`,
        maxWidth: '100%',
        marginInline: 'auto'
      }
    : { aspectRatio: `${overlayCore.width} / ${overlayCore.height}` }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      {isEnded && (
        <div className='rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
          This stream has ended, overlay is read-only.
        </div>
      )}
      <div className='grid grid-cols-1 gap-1.5 lg:grid-cols-2'>
        <section className='flex flex-col rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
          <div className='mb-3 flex items-center justify-between gap-2'>
            <div className='flex min-w-0 items-center gap-2'>
              <h3 className='text-base font-semibold text-dm-text-primary'>Preview</h3>
              <PanePill variant='preview' />
              {selectedScene !== null && (
                <span className='truncate text-xs text-dm-text-accent'>{selectedScene.name}</span>
              )}
            </div>
            <div className='flex shrink-0 items-center gap-2'>
              <div className='flex items-center gap-1'>
                <button
                  type='button'
                  onClick={() => handleHistoryStep('undo')}
                  disabled={undoEntry == null}
                  title={undoTitle}
                  aria-label={undoTitle}
                  data-testid='scene-undo-button'
                  className='rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:opacity-30'
                >
                  <ArrowUturnLeftIcon className='size-4' aria-hidden='true' />
                </button>
                <button
                  type='button'
                  onClick={() => handleHistoryStep('redo')}
                  disabled={redoEntry == null}
                  title={redoTitle}
                  aria-label={redoTitle}
                  data-testid='scene-redo-button'
                  className='rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:opacity-30'
                >
                  <ArrowUturnRightIcon className='size-4' aria-hidden='true' />
                </button>
              </div>
              {isDirty && (
                <span className='inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-300'>
                  <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400' />
                  Unsaved changes
                </span>
              )}
              <button
                type='button'
                onClick={handleDiscardDraft}
                disabled={!isDirty || elementsSaving || isEnded}
                title='Discard unsaved changes'
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
              >
                Discard
              </button>
              <button
                type='button'
                onClick={() => { void handleSaveDraft() }}
                disabled={!isDirty || elementsSaving || isEnded}
                title='Save this scene’s layout'
                className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                data-testid='save-scene-elements-button'
              >
                {elementsSaving ? <LoadingSpinner /> : <CheckIcon className='size-3.5' aria-hidden='true' />}
                Save
              </button>
            </div>
          </div>
          <div ref={previewPaneRef} style={paneBoxStyle} onPointerDown={handleCanvasPointerDown} className='relative overflow-hidden rounded-lg'>
            <SceneCanvas
              scene={workingScene}
              event={event}
              streamChats={streamChats}
              streamWalls={streamWalls}
              accountBoards={accountBoards}
              canvasWidth={overlayCore.width}
              canvasHeight={overlayCore.height}
              interactive={!isEnded}
              override={elementOverride}
              guides={activeGuides}
              selectedElementKey={selectedElementKey}
              onElementPointerDown={handleElementPointerDown}
              onElementPointerMove={handleElementPointerMove}
              onElementPointerUp={handleElementPointerUp}
              onElementPointerCancel={handleElementPointerCancel}
              onResizePointerDown={handleResizePointerDown}
              onResizePointerMove={handleResizePointerMove}
              onResizePointerUp={handleResizePointerUp}
              onResizePointerCancel={handleResizePointerCancel}
              pollWeightsPreview={pollWeightsPreview}
              onBoundaryPointerDown={handleBoundaryPointerDown}
              onBoundaryPointerMove={handleBoundaryPointerMove}
              onBoundaryPointerUp={handleBoundaryPointerUp}
              onBoundaryPointerCancel={handleBoundaryPointerCancel}
              measuredHeights={contentHeights}
              onMeasure={handleMeasure}
              onMerchEnvelope={handleMerchEnvelope}
            />
          </div>
          <div className='mt-3 flex flex-wrap items-center gap-2'>
            <button
              type='button'
              onClick={() => { void handleTakeLive('CUT') }}
              disabled={selectedScene === null || (selectedIsLive && !selectedHasDrift) || liveBusy !== null || isEnded || selectedIsDirty}
              title={selectedIsDirty ? 'Save your changes first — Take live broadcasts the last saved layout' : undefined}
              className={classNames(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold',
                selectedScene !== null && (!selectedIsLive || selectedHasDrift) && liveBusy === null && !isEnded && !selectedIsDirty
                  ? 'bg-amber-400 text-black hover:bg-amber-300'
                  : 'bg-white/10 text-dm-text-accent'
              )}
              data-testid='take-scene-live-button'
            >
              {liveBusy === 'take' ? <LoadingSpinner /> : <SignalIcon className='size-3.5' aria-hidden='true' />}
              {selectedScene !== null
                ? selectedHasDrift ? `Update "${selectedScene.name}" live` : `Take "${selectedScene.name}" live`
                : 'Take live'}
            </button>
            <button
              type='button'
              onClick={() => { void handleTakeLive('FADE') }}
              disabled={selectedScene === null || (selectedIsLive && !selectedHasDrift) || liveBusy !== null || isEnded || selectedIsDirty}
              title={selectedIsDirty ? 'Save your changes first, take live broadcasts the last saved layout' : 'Take live with a fade / cross-fade'}
              className='inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 px-2.5 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-400/10 disabled:cursor-not-allowed disabled:opacity-60'
              data-testid='fade-scene-to-live-button'
            >
              {liveBusy === 'fade' ? <LoadingSpinner /> : <SignalIcon className='size-3.5' aria-hidden='true' />}
              {selectedScene !== null
                ? selectedHasDrift ? `Fade updated "${selectedScene.name}" live` : `Fade "${selectedScene.name}" to live`
                : 'Fade to live'}
            </button>
            <div className='ml-auto flex items-center gap-1'>
              <span className='text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>Layer</span>
              <button
                type='button'
                onClick={() => { if (selectedElement !== null) handleMoveLayer(selectedElement, 1) }}
                disabled={isEnded || selectedElement === null || selectedLayerIndex === -1 || selectedLayerIndex === sceneQueueRows.length - 1}
                title='Bring forward'
                className='rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:opacity-30'
              >
                <ChevronUpIcon className='size-4' aria-hidden='true' />
                <span className='sr-only'>Bring forward</span>
              </button>
              <button
                type='button'
                onClick={() => { if (selectedElement !== null) handleMoveLayer(selectedElement, -1) }}
                disabled={isEnded || selectedElement === null || selectedLayerIndex <= 0}
                title='Send backward'
                className='rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:opacity-30'
              >
                <ChevronDownIcon className='size-4' aria-hidden='true' />
                <span className='sr-only'>Send backward</span>
              </button>
            </div>
          </div>
          {liveError !== null && (
            <div className='mt-2'>
              <ErrorInline onDismiss={() => setLiveError(null)}>{liveError}</ErrorInline>
            </div>
          )}
        </section>

        <section className='flex flex-col rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
          <div className='mb-3 flex items-center justify-between gap-2'>
            <div className='flex min-w-0 items-center gap-2'>
              <h3 className='text-base font-semibold text-dm-text-primary'>Live Output</h3>
              <PanePill variant='live' />
              <span className='truncate text-xs text-dm-text-accent'>
                {liveSnapshot !== null ? liveSnapshot.name : 'Off air'}
              </span>
            </div>
            {overlayUrl !== null && (
              <div className='flex items-center gap-1.5'>
                <button
                  type='button'
                  onClick={() => { void handleResync() }}
                  disabled={resyncBusy || isEnded}
                  title='Resync overlays, re-push current state to every browser source'
                  aria-label='Resync overlays'
                  className='inline-flex items-center text-dm-text-accent hover:text-dm-text-secondary disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {resyncBusy
                    ? <LoadingSpinner />
                    : <ArrowPathIcon className='size-4' aria-hidden='true' />}
                </button>
                <CopyButton value={overlayUrl} label='composite overlay link' />
                <a
                  href={overlayUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  title='Open composite overlay in a new window'
                  aria-label='Open composite overlay in a new window'
                  className='inline-flex items-center text-dm-text-accent hover:text-dm-text-secondary'
                >
                  <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
                </a>
              </div>
            )}
          </div>
          <div style={paneBoxStyle} className='relative overflow-hidden rounded-lg'>

            <ChatGateStatusPane onStatus={setChatGateStatus}>
              <ChatLineRemovePane eventId={event.eventId} onRemove={handleRemoveChatLine} offered={chatLineRemoveOffered}>
                <SourceMarkLinkPane>
                  <SceneCanvas
                    scene={liveSnapshot}
                    event={event}
                    streamChats={streamChats}
                    streamWalls={streamWalls}
                    accountBoards={accountBoards}
                    canvasWidth={overlayCore.width}
                    canvasHeight={overlayCore.height}
                    interactive={false}
                    override={null}
                    guides={null}
                    selectedElementKey={null}
                    presence={livePresence}
                  />
                </SourceMarkLinkPane>
              </ChatLineRemovePane>
            </ChatGateStatusPane>
          </div>

          {removeFailed && (
            <div className='mt-2' role='alert' data-testid='chat-line-remove-failed'>
              <div className='flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
                <span className='flex-1'>{CHAT_LINE_REMOVE_FAILED}</span>
                <button
                  type='button'
                  onClick={() => { setRemoveFailed(false); chatLineRemover.retry() }}
                  className='shrink-0 rounded border border-red-400/40 px-2 py-0.5 font-semibold text-red-200 hover:bg-red-500/20'
                >
                  {CHAT_LINE_REMOVE_RETRY}
                </button>
              </div>
            </div>
          )}
          <ChatGateStatusLines status={chatGateStatus} />
          <div className='mt-3 flex flex-wrap items-center gap-2'>
            <button
              type='button'
              onClick={() => { void handleClearLive() }}
              disabled={overlayCore.liveSceneId === null || liveBusy !== null || isEnded}
              title='Stop showing any scene on the live overlay (instant cut)'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
              data-testid='clear-live-button'
            >
              {liveBusy === 'clear' ? <LoadingSpinner /> : <EyeSlashIcon className='size-3.5' aria-hidden='true' />}
              Hide
            </button>
          </div>
        </section>
      </div>
      <div className='grid grid-cols-1 items-stretch gap-1.5 lg:grid-cols-4'>
        <section className='flex flex-col rounded-xl border border-white/5 bg-dm-bg-secondary p-4 lg:col-span-1'>
          <div className='mb-3 flex items-center justify-between gap-2'>
            <h4 className='text-sm font-semibold text-dm-text-primary'>Scenes</h4>
            <button
              type='button'
              onClick={() => { void handleCreateScene() }}
              disabled={railBusy !== null || isEnded || orderedScenes.length >= sceneCap}
              title='Add scene'
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-2.5 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {railBusy === 'create' ? <LoadingSpinner /> : <PlusIcon className='size-3.5' aria-hidden='true' />}
              <span className='sr-only'>Add scene</span>
            </button>
          </div>
          {railError !== null && (
            <div className='mb-2'>
              <ErrorInline onDismiss={() => setRailError(null)}>{railError}</ErrorInline>
            </div>
          )}
          {orderedScenes.length === 0
            ? (
              <div className='flex flex-col items-center gap-2 rounded-lg border border-dashed border-white/10 px-3 py-8 text-center text-xs text-dm-text-secondary'>
                <RectangleGroupIcon className='size-6 text-dm-text-accent' aria-hidden='true' />
                <p>No scenes yet.</p>
                {!isEnded && <p className='text-dm-text-accent'>Click + to create the first one.</p>}
              </div>
              )
            : (
              <ul className='space-y-1.5'>
                {orderedScenes.map((scene) => {
                  const isSelected = selectedScene !== null && selectedScene.sceneId === scene.sceneId
                  const isLive = overlayCore.liveSceneId === scene.sceneId
                  const isRenaming = renamingSceneId === scene.sceneId
                  const orderIndex = fullSceneOrder.indexOf(scene.sceneId)
                  return (
                    <li key={scene.sceneId}>
                      <div
                        className={classNames(
                          'flex items-center gap-1.5 rounded-md border px-2.5 py-2',
                          isSelected
                            ? 'border-brand/50 bg-brand/10'
                            : 'border-white/5 bg-dm-bg-accent/30 hover:bg-dm-bg-accent/50'
                        )}
                      >
                        {isRenaming
                          ? (
                            <SceneRenameInput
                              initial={scene.name}
                              busy={railBusy === scene.sceneId}
                              onSave={(name) => { void handleRenameScene(scene.sceneId, name) }}
                              onCancel={() => setRenamingSceneId(null)}
                            />
                            )
                          : (
                            <>
                              <button
                                type='button'
                                onClick={() => handleSelectScene(scene.sceneId)}
                                className='min-w-0 flex-1 truncate text-left text-sm text-dm-text-primary'
                              >
                                {scene.name}
                              </button>
                              {isLive && (
                                <span className='shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-400 ring-1 ring-inset ring-emerald-500/30'>
                                  {liveSourceDrift ? 'Live·edited' : 'Live'}
                                </span>
                              )}
                              {!isEnded && (
                                <>
                                  <button
                                    type='button'
                                    onClick={() => handleMoveScene(scene.sceneId, -1)}
                                    disabled={orderIndex === 0}
                                    title='Move scene up'
                                    className='shrink-0 rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:opacity-30'
                                  >
                                    <ChevronUpIcon className='size-3.5' aria-hidden='true' />
                                    <span className='sr-only'>Move scene up</span>
                                  </button>
                                  <button
                                    type='button'
                                    onClick={() => handleMoveScene(scene.sceneId, 1)}
                                    disabled={orderIndex === fullSceneOrder.length - 1}
                                    title='Move scene down'
                                    className='shrink-0 rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:opacity-30'
                                  >
                                    <ChevronDownIcon className='size-3.5' aria-hidden='true' />
                                    <span className='sr-only'>Move scene down</span>
                                  </button>
                                  <button
                                    type='button'
                                    onClick={() => setRenamingSceneId(scene.sceneId)}
                                    title='Rename scene'
                                    className='shrink-0 rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
                                  >
                                    <PencilIcon className='size-3.5' aria-hidden='true' />
                                  </button>
                                  <button
                                    type='button'
                                    onClick={() => { void handleDeleteScene(scene) }}
                                    disabled={railBusy !== null}
                                    title='Delete scene'
                                    className='shrink-0 rounded p-1 text-dm-text-accent hover:bg-red-500/10 hover:text-red-300 disabled:opacity-60'
                                  >
                                    {railBusy === scene.sceneId ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
                                  </button>
                                </>
                              )}
                            </>
                            )}
                      </div>
                    </li>
                  )
                })}
              </ul>
              )}
        </section>
        <section className='flex flex-col rounded-xl border border-white/5 bg-dm-bg-secondary p-4 lg:col-span-2'>
          <div className='mb-3 flex items-center justify-between gap-2'>
            <div>
              <h4 className='text-sm font-semibold text-dm-text-primary'>Scene Queue</h4>
              <p className='text-[11px] text-dm-text-accent'>
                {selectedScene !== null
                  ? `Elements in "${selectedScene.name}" — drag to reposition, handles to resize, arrows to nudge; changes stay local until Save.`
                  : 'Select a scene to manage its elements.'}
              </p>
            </div>
            <AddElementMenu
              candidates={addCandidates}
              merchVisible={merchVisible}
              camCtlVisible={staffUncapped}
              tickerBarsVisible={tickerBarsVisible}
              disabled={
                selectedScene === null || isEnded ||
                sceneQueueRows.length >= MAX_ELEMENTS_PER_SCENE
              }
              onAdd={handleAddElement}
            />
          </div>
          {queueError !== null && (
            <div className='mb-2'>
              <ErrorInline onDismiss={() => setQueueError(null)}>{queueError}</ErrorInline>
            </div>
          )}
          {selectedScene === null || sceneQueueRows.length === 0
            ? (
              <div className='flex flex-col items-center gap-2 rounded-lg border border-dashed border-white/10 px-3 py-8 text-center text-xs text-dm-text-secondary'>
                <RectangleGroupIcon className='size-6 text-dm-text-accent' aria-hidden='true' />
                <p>{selectedScene === null ? 'No scene selected.' : 'No elements in this scene yet.'}</p>
              </div>
              )
            : (
              <ul className='space-y-1.5'>
                {sceneQueueRows.map((el) => {
                  const key = elementKeyOf(el)
                  const name = resolveElementName(event, streamChats, streamWalls, accountBoards, el)
                  const isDangling = name === null
                  const isRowSelected = selectedElementKey === key
                  const triviaRow = el.elementKind === 'TRIVIA'
                    ? event.trivias.find((t) => t.triviaId === el.elementId) ?? null
                    : null
                  const votingSummary = el.elementKind === 'POLL'
                    ? event.polls.find((p) => p.pollId === el.elementId) ?? null
                    : el.elementKind === 'TUG'
                      ? event.tugs.find((t) => t.tugId === el.elementId) ?? null
                      : null
                  const giveawayRow = el.elementKind === 'GIVEAWAY'
                    ? event.giveaways.find((g) => g.giveawayId === el.elementId) ?? null
                    : null
                  return (
                    <li key={key}>
                      <div
                        onClick={() => setSelectedElementKey(key)}
                        className={classNames(
                          'flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2',
                          isRowSelected
                            ? 'border-brand/50 bg-brand/10'
                            : 'border-white/5 bg-dm-bg-accent/30 hover:bg-dm-bg-accent/50'
                        )}
                      >
                        <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
                          {KIND_LABELS[el.elementKind]}
                        </span>
                        <span className={classNames(
                          'min-w-0 flex-1 truncate text-sm',
                          isDangling ? 'italic text-dm-text-accent' : 'text-dm-text-primary'
                        )}
                        >
                          {isDangling ? 'Deleted element' : name}
                        </span>
                        {votingSummary !== null && (
                          <VotingStatusChip status={votingStatusOf(votingSummary)} />
                        )}
                        {triviaRow !== null && (
                          <TriviaStatusChip phase={triviaPhaseOf(triviaRow, triviaNowMs)} />
                        )}
                        {giveawayRow !== null && (
                          <span
                            data-testid='giveaway-queue-chip'
                            className={classNames(
                              'shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ring-1 ring-inset',
                              GIVEAWAY_STATUS_PILL[giveawayRow.status]
                            )}
                          >
                            {GIVEAWAY_STATUS_LABEL[giveawayRow.status]}
                          </span>
                        )}
                        {!isDangling && !isEnded && (
                          <button
                            type='button'
                            onClick={(e) => { e.stopPropagation(); handleToggleVisible(el) }}
                            title={el.visible ? 'Hide in this scene' : 'Show in this scene'}
                            className='shrink-0 rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
                          >
                            {el.visible
                              ? <EyeIcon className='size-4' aria-hidden='true' />
                              : <EyeSlashIcon className='size-4' aria-hidden='true' />}
                          </button>
                        )}
                        {!isDangling && (
                          <Link
                            to={elementEditPath(event.eventId, el)}
                            onClick={(e) => e.stopPropagation()}
                            title='Edit this element'
                            className='shrink-0 rounded p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
                          >
                            <PencilSquareIcon className='size-4' aria-hidden='true' />
                            <span className='sr-only'>Edit</span>
                          </Link>
                        )}
                        {!isEnded && (
                          <button
                            type='button'
                            onClick={(e) => { e.stopPropagation(); handleRemoveElement(el) }}
                            title='Remove from scene'
                            className='shrink-0 rounded p-1 text-dm-text-accent hover:bg-red-500/10 hover:text-red-300'
                          >
                            <XMarkIcon className='size-4' aria-hidden='true' />
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
              )}
        </section>
        <section className='flex flex-col rounded-xl border border-white/5 bg-dm-bg-secondary p-4 lg:col-span-1'>
          <h4 className='mb-3 text-sm font-semibold text-dm-text-primary'>Selected Element Controls</h4>
          {selectedElement === null && (
            <div className='flex flex-col items-center gap-2 rounded-lg border border-dashed border-white/10 px-4 py-8 text-center text-sm text-dm-text-secondary'>
              <SignalIcon className='size-6 text-dm-text-accent' aria-hidden='true' />
              <p>No element selected.</p>
              <p className='text-xs text-dm-text-accent'>
                Click a row in the scene queue to take control of it.
              </p>
            </div>
          )}
          {selectedElement !== null && selectedElement.elementKind === 'POLL' && (
            selectedPoll !== null
              ? (
                <div className='flex flex-col gap-3'>
                  <div>
                    <div className='text-sm font-medium text-dm-text-primary'>{elementDisplayName(selectedPoll.name, selectedPoll.question)}</div>
                    <div className='mt-0.5 text-xs text-dm-text-accent'>{selectedPoll.question}</div>
                  </div>
                  <div className='flex items-center gap-2 text-xs'>
                    <span className='text-dm-text-accent'>Voting status:</span>
                    <VotingStatusChip status={votingStatusOf(selectedPoll)} />
                  </div>
                  <ControlButtons
                    isActive={selectedPoll.status === 'ACTIVE'}
                    busy={controlsBusy}
                    locked={isEnded}
                    onStart={() => { void handlePollStart() }}
                    onStop={() => { void handlePollStop() }}
                    onReset={() => { void handlePollReset() }}
                  />
                  {!isEnded && selectedPoll.template === 'TICKER' && (
                    <div data-testid='poll-block-widths'>
                      <div className='mb-1.5 text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                        Block widths
                      </div>
                      <div className='space-y-1'>
                        {Array.from({ length: selectedPoll.options.length + 1 }, (_, b) => {
                          const leftName = b === 0 ? 'Question' : `Option ${b}`
                          const rightName = b === selectedPoll.options.length ? 'CTA' : `Option ${b + 1}`
                          const locked = selectedPoll.status === 'ACTIVE'
                          const lockTitle = 'Stop the poll before reshaping its blocks.'
                          return (
                            <div key={b} className='flex items-center gap-1.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-2 py-1'>
                              <span className='min-w-0 flex-1 truncate text-[11px] text-dm-text-secondary'>
                                {leftName} | {rightName}
                              </span>
                              <button
                                type='button'
                                onClick={() => handleWeightsNudge(b, -1)}
                                disabled={weightsBusy || locked}
                                title={locked ? lockTitle : `Shift the boundary between ${leftName} and ${rightName} left`}
                                aria-label={`Shift the boundary between ${leftName} and ${rightName} left`}
                                className='inline-flex size-6 shrink-0 items-center justify-center rounded-md border border-white/10 text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
                              >
                                <ChevronLeftIcon className='size-3' aria-hidden='true' />
                              </button>
                              <button
                                type='button'
                                onClick={() => handleWeightsNudge(b, 1)}
                                disabled={weightsBusy || locked}
                                title={locked ? lockTitle : `Shift the boundary between ${leftName} and ${rightName} right`}
                                aria-label={`Shift the boundary between ${leftName} and ${rightName} right`}
                                className='inline-flex size-6 shrink-0 items-center justify-center rounded-md border border-white/10 text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
                              >
                                <ChevronRightIcon className='size-3' aria-hidden='true' />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                      <button
                        type='button'
                        onClick={handleWeightsReset}
                        disabled={weightsBusy || selectedPoll.status === 'ACTIVE' || selectedEffectiveWeights == null}
                        title={selectedPoll.status === 'ACTIVE'
                          ? 'Stop the poll before reshaping its blocks.'
                          : selectedEffectiveWeights == null
                            ? 'This ticker already uses the automatic equal layout.'
                            : 'Preview the automatic layout (Save makes it stick)'}
                        data-testid='poll-weights-reset'
                        className='mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
                      >
                        Reset to equal
                      </button>
                      <div className='mt-2 flex items-center justify-end gap-2'>
                        {selectedWeightsDirty && (
                          <span className='mr-auto inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-300' data-testid='poll-weights-dirty-chip'>
                            <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400' />
                            Unsaved changes
                          </span>
                        )}
                        <button
                          type='button'
                          onClick={handleWeightsDiscard}
                          disabled={!selectedWeightsDirty || weightsBusy}
                          title='Discard the unsaved block widths'
                          data-testid='poll-weights-discard'
                          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                        >
                          Discard
                        </button>
                        <button
                          type='button'
                          onClick={handleWeightsSave}
                          disabled={!selectedWeightsDirty || weightsBusy || selectedPoll.status === 'ACTIVE' || isEnded}
                          title='Save these block widths to the poll'
                          data-testid='poll-weights-save'
                          className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                        >
                          {weightsBusy ? <LoadingSpinner /> : <CheckIcon className='size-3.5' aria-hidden='true' />}
                          Save
                        </button>
                      </div>
                      <p className='mt-1.5 text-[11px] leading-relaxed text-dm-text-accent'>
                        Drag the dividers on the placed ticker in the Preview pane, or
                        nudge a boundary here. Changes preview locally; Save persists
                        one set of proportions on the poll, and only then do both
                        overlay surfaces pick it up.
                      </p>
                      {weightsError !== null && (
                        <div className='mt-2'>
                          <ErrorInline onDismiss={() => setWeightsError(null)}>{weightsError}</ErrorInline>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )
              : <p className='text-xs text-dm-text-accent'>This poll no longer exists.</p>
          )}
          {selectedElement !== null && selectedElement.elementKind === 'TRIVIA' && (
            selectedTrivia !== null
              ? (
                <div className='flex flex-col gap-3' data-testid='trivia-panel'>
                  <div>
                    <div className='text-sm font-medium text-dm-text-primary'>{elementDisplayName(selectedTrivia.name, selectedTrivia.question)}</div>
                    <div className='mt-0.5 text-xs text-dm-text-accent'>{selectedTrivia.question}</div>
                  </div>
                  <div className='flex flex-wrap items-center gap-2 text-xs'>
                    <span className='text-dm-text-accent'>Question status:</span>
                    <TriviaStatusChip
                      phase={triviaPhaseOf(selectedTrivia, triviaNowMs)}
                      youtubeTimedOut={triviaYouTubeTimedOut(selectedTrivia, triviaNowMs)}
                    />

                    {selectedTrivia.revealedAt != null && triviaYouTubeCounting(selectedTrivia, triviaNowMs) && (
                      <span
                        data-testid='trivia-panel-still-counting'
                        title={TRIVIA_STILL_COUNTING_TOOLTIP}
                        className='rounded-full bg-cyan-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-300 ring-1 ring-inset ring-cyan-400/30'
                      >
                        {TRIVIA_STILL_COUNTING_LABEL}
                      </span>
                    )}
                    {selectedTrivia.hideLiveResults === true && selectedTrivia.revealedAt == null && (
                      <span className='rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
                        Results hidden on overlay
                      </span>
                    )}
                  </div>
                  <div className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2' data-testid='trivia-panel-readout'>
                    <div className='text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
                      Live answers (private{selectedTrivia.hideLiveResults === true && selectedTrivia.revealedAt == null ? '; viewers see none of this' : ''})
                      {selectedTriviaPhase === 'counting' ? ' · counting YouTube' : ''}
                    </div>
                    <ul className='mt-1 space-y-0.5'>
                      {selectedTrivia.options.map((o) => (
                        <li key={o.optionId} className='flex items-center justify-between gap-2 text-xs'>
                          <span className='flex min-w-0 items-center gap-1.5'>
                            <span className='shrink-0 font-mono text-dm-text-accent'>{o.command}</span>
                            <span className='truncate text-dm-text-primary'>{o.displayText}</span>
                            {o.isCorrect === true && (
                              <span className='shrink-0 text-[9px] font-semibold uppercase tracking-wider text-emerald-400'>correct</span>
                            )}
                          </span>
                          <span className='shrink-0 tabular-nums text-dm-text-secondary'>
                            Twitch {o.voteCountByPlatform?.twitch ?? o.voteCount} · YouTube {o.voteCountByPlatform?.youtube ?? 0}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    <button
                      type='button'
                      onClick={() => { void handleTriviaStart() }}
                      disabled={controlsBusy !== null || isEnded || selectedTrivia.status === 'ACTIVE' || selectedTrivia.revealedAt != null}
                      title={selectedTrivia.revealedAt != null ? 'A revealed question cannot be restarted as it is. Reset it (answers are deleted) or duplicate it to run it again.' : 'Start taking answers'}
                      data-testid='trivia-ctl-start'
                      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'start' ? <LoadingSpinner /> : <PlayCircleIcon className='size-4' aria-hidden='true' />}
                      Start
                    </button>
                    <button
                      type='button'
                      onClick={() => { void handleTriviaClose() }}
                      disabled={controlsBusy !== null || isEnded || selectedTrivia.status !== 'ACTIVE' || selectedTrivia.closedAt != null}
                      title='Stop taking answers (the grace window still counts late ones)'
                      data-testid='trivia-ctl-close'
                      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-400 px-3 py-2 text-xs font-semibold text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'close' ? <LoadingSpinner /> : <LockClosedIcon className='size-4' aria-hidden='true' />}
                      Close
                    </button>
                    <div className='flex flex-col gap-1'>
                      {selectedTriviaPhase === 'counting'
                        ? (
                          <button
                            type='button'
                            disabled
                            title={triviaCountingTooltip(selectedTrivia, triviaNowMs)}
                            data-testid='trivia-ctl-reveal'
                            className='inline-flex items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold text-black disabled:cursor-not-allowed disabled:opacity-50'
                          >
                            <LoadingSpinner />
                            {triviaCountingButtonLabel(selectedTrivia, triviaNowMs)}
                          </button>
                          )
                        : (
                          <button
                            type='button'
                            onClick={() => { void handleTriviaReveal() }}
                            disabled={controlsBusy != null || !triviaRevealArmed(selectedTrivia, triviaNowMs)}
                            title={selectedTrivia.revealedAt != null
                              ? 'Already revealed'
                              : 'Show the correct answer (arms once the question has run and been closed, its grace window has ended and any YouTube answers are counted; works after the stream ends)'}
                            data-testid='trivia-ctl-reveal'
                            className='inline-flex items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-50'
                          >
                            {controlsBusy === 'reveal' ? <LoadingSpinner /> : <SparklesIcon className='size-4' aria-hidden='true' />}
                            Reveal
                          </button>
                          )}
                      {triviaRevealNowOffered(selectedTrivia, triviaNowMs) && (
                        <button
                          type='button'
                          onClick={() => setTriviaRevealNowFor(selectedTrivia.triviaId)}
                          disabled={controlsBusy != null}
                          title={TRIVIA_REVEAL_NOW_TOOLTIP}
                          data-testid='trivia-ctl-reveal-now'
                          className='inline-flex items-center justify-center gap-1.5 rounded-md border border-brand/40 px-3 py-1 text-[11px] font-semibold text-brand hover:border-brand/70 hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-50'
                        >
                          {controlsBusy === 'reveal' ? <LoadingSpinner /> : <SparklesIcon className='size-3.5' aria-hidden='true' />}
                          Reveal now
                        </button>
                      )}
                    </div>
                    <button
                      type='button'
                      onClick={() => { void handleTriviaReset() }}
                      disabled={controlsBusy !== null || isEnded}
                      title={selectedTrivia.revealedAt != null
                        ? 'Delete every answer, remove it from the leaderboard and return it to a startable draft'
                        : 'Delete every recorded answer and start the count over'}
                      data-testid='trivia-ctl-reset'
                      className='inline-flex items-center justify-center gap-1.5 self-start rounded-md bg-red-500 px-3 py-2 text-xs font-semibold text-black hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'reset' ? <LoadingSpinner /> : <ArrowPathIcon className='size-4' aria-hidden='true' />}
                      Reset
                    </button>
                    <button
                      type='button'
                      onClick={() => {
                        const ok = globalThis.confirm(
                          `Cancel "${selectedTrivia.question}"?\n\n` +
                          'This stops the run and returns the question to a startable draft. ' +
                          'Nothing is deleted: answers recorded so far stay stored, their counts stay on the dashboard, and nothing is scored on the leaderboard until you reveal.\n\n' +
                          'Heads up: each viewer answers a question once. If you start this question again, viewers who already answered are locked to their original answer; use Reset to clear all answers for a truly fresh run.'
                        )
                        if (ok) void handleTriviaCancel()
                      }}
                      disabled={controlsBusy !== null || isEnded || selectedTrivia.status !== 'ACTIVE' || selectedTrivia.revealedAt != null}
                      title={selectedTrivia.status === 'ACTIVE' && selectedTrivia.revealedAt == null
                        ? 'Cancel this run: back to a startable draft, answers kept'
                        : 'Cancel is available while the question is live, in grace or locked'}
                      data-testid='trivia-ctl-cancel'
                      className='col-span-2 inline-flex items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'stop' ? <LoadingSpinner /> : <XMarkIcon className='size-4' aria-hidden='true' />}
                      Cancel question
                    </button>
                  </div>
                  <p className='text-[11px] leading-relaxed text-dm-text-accent'>
                    Close freezes the question after its grace window; Reveal puts the
                    correct answer on the overlay and scores it on the leaderboard.
                    On a question that counts YouTube, FlightDeck holds Reveal a few
                    seconds more while it counts the last YouTube answers; Reveal now
                    skips the wait if that runs long.
                    Cancel abandons an un-revealed run, keeping its answers, so the
                    question can start again. Reset deletes every answer and returns
                    the question to a startable draft, even after Reveal (it then
                    leaves the leaderboard). Reveal and the answer fix stay available
                    from the Trivia page after the stream ends.
                  </p>

                  {triviaRevealNowOpen && (
                    <ConfirmDialog
                      title={TRIVIA_REVEAL_NOW_CONFIRM.title}
                      confirmLabel={TRIVIA_REVEAL_NOW_CONFIRM.confirmLabel}
                      cancelLabel={TRIVIA_REVEAL_NOW_CONFIRM.cancelLabel}
                      onConfirm={confirmTriviaRevealNow}
                      onCancel={() => setTriviaRevealNowFor(null)}
                      testId='trivia-reveal-now-dialog'
                    >
                      {TRIVIA_REVEAL_NOW_CONFIRM.body}
                    </ConfirmDialog>
                  )}
                </div>
                )
              : <p className='text-xs text-dm-text-accent'>This trivia question no longer exists.</p>
          )}
          {selectedElement !== null && selectedElement.elementKind === 'TUG' && (
            selectedTug !== null
              ? (
                <div className='flex flex-col gap-3'>
                  <div>
                    <div className='text-sm font-medium text-dm-text-primary'>{elementDisplayName(selectedTug.name, selectedTug.question)}</div>
                    <div className='mt-0.5 text-xs text-dm-text-accent'>{selectedTug.question}</div>
                  </div>
                  <div className='flex items-center gap-2 text-xs'>
                    <span className='text-dm-text-accent'>Voting status:</span>
                    <VotingStatusChip status={votingStatusOf(selectedTug)} />
                  </div>
                  <ControlButtons
                    isActive={selectedTug.status === 'ACTIVE'}
                    busy={controlsBusy}
                    locked={isEnded}
                    onStart={() => { void handleTugStart() }}
                    onStop={() => { void handleTugStop() }}
                    onReset={() => { void handleTugReset() }}
                  />
                </div>
                )
              : <p className='text-xs text-dm-text-accent'>This tug of war no longer exists.</p>
          )}
          {selectedElement !== null && selectedElement.elementKind === 'BOARD' && (
            selectedBoard !== null
              ? (
                <div className='flex flex-col gap-3'>
                  <div className='text-sm font-medium text-dm-text-primary'>{selectedBoard.name}</div>
                  <BoardScoreControls
                    board={selectedBoard}
                    locked={isEnded}
                    auth0Token={auth0Token}
                    setEvent={setEvent}
                  />
                </div>
                )
              : <p className='text-xs text-dm-text-accent'>This scoreboard no longer exists.</p>
          )}
          {selectedElement !== null && (selectedElement.elementKind === 'MERCH' || selectedElement.elementKind === 'MERCH_TICKER') && (
            selectedMerchBoard !== null
              ? (
                <MerchElementPanel
                  board={selectedMerchBoard}
                  auth0Token={auth0Token}
                  liveEnvelope={merchEnvelopes[selectedMerchBoard.merchOverlayId] ?? null}
                  onBoardChanged={(overlay) => {
                    setAccountBoards((prev) => prev.map((b) => b.merchOverlayId === overlay.merchOverlayId ? overlay : b))
                  }}
                />
                )
              : <p className='text-xs text-dm-text-accent'>This merch board no longer exists.</p>
          )}
          {selectedElement !== null && selectedElement.elementKind === 'CAMERA_CONTROL' && (
            selectedCamCtl !== null
              ? (
                <div className='flex flex-col gap-3'>
                  <div>
                    <div className='text-sm font-medium text-dm-text-primary'>{selectedCamCtl.name}</div>
                    <div className='mt-0.5 text-xs text-dm-text-accent'>
                      Chat steers the camera, commands show on the cam-op page, never on this overlay.
                    </div>
                  </div>
                  <div className='flex items-center gap-2 text-xs'>
                    <span className='text-dm-text-accent'>Status:</span>
                    <VotingStatusChip status={votingStatusOf(selectedCamCtl)} />
                  </div>
                  <div className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
                    <div className='text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>Current command</div>
                    <div className='mt-0.5 flex items-baseline gap-2'>
                      <span className='font-mono text-base font-semibold text-dm-text-primary'>
                        {selectedCamCtl.currentCommandIndex !== null
                          ? selectedCamCtl.commands[selectedCamCtl.currentCommandIndex]?.command ?? '—'
                          : '—'}
                      </span>
                      {(selectedCamCtl.streak ?? 0) > 1 && (
                        <span className='rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold text-brand ring-1 ring-inset ring-brand/30'>
                          x{selectedCamCtl.streak} streak
                        </span>
                      )}
                    </div>
                  </div>
                  <ControlButtons
                    isActive={selectedCamCtl.status === 'ACTIVE'}
                    busy={controlsBusy}
                    locked={isEnded}
                    onStart={() => { void handleCamCtlStart() }}
                    onStop={() => { void handleCamCtlStop() }}
                    onReset={() => { void handleCamCtlReset() }}
                  />
                </div>
                )
              : <p className='text-xs text-dm-text-accent'>This camera control no longer exists.</p>
          )}
          {selectedElement !== null && selectedElement.elementKind === 'GIVEAWAY' && (
            selectedGiveaway !== null
              ? (
                <div className='flex flex-col gap-3' data-testid='giveaway-panel'>
                  <div>
                    <div className='text-sm font-medium text-dm-text-primary'>{selectedGiveaway.name}</div>
                    <div className='mt-0.5 text-xs text-dm-text-accent'>
                      {selectedGiveaway.prizeName} · <span className='font-mono'>{selectedGiveaway.entryCommand}</span> · {selectedGiveaway.winnerCount} winner{selectedGiveaway.winnerCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className='flex flex-wrap items-center gap-2 text-xs'>
                    <span className='text-dm-text-accent'>Status:</span>
                    <span
                      data-testid='giveaway-panel-status'
                      className={classNames(
                        'rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
                        GIVEAWAY_STATUS_PILL[selectedGiveaway.status]
                      )}
                    >
                      {GIVEAWAY_STATUS_LABEL[selectedGiveaway.status]}
                    </span>
                    <span className='text-dm-text-accent'>·</span>
                    <span className='font-mono tabular-nums text-dm-text-primary' data-testid='giveaway-panel-entrant-count'>
                      {selectedGiveaway.entrantCount.toLocaleString()}
                    </span>
                    <span className='text-dm-text-accent'>entrants</span>
                    {selectedGiveaway.hasDraw && selectedGiveaway.status !== 'ENDED' && (
                      <span data-testid='giveaway-panel-drawn-hint' className='rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300 ring-1 ring-inset ring-amber-500/30'>
                        winners drawn (unpublished)
                      </span>
                    )}
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    <button
                      type='button'
                      onClick={() => { void handleGiveawayStart() }}
                      disabled={controlsBusy !== null || isEnded || selectedGiveaway.hasDraw || selectedGiveaway.draw != null || !(selectedGiveaway.status === 'UPCOMING' || selectedGiveaway.status === 'CLOSED')}
                      data-testid='giveaway-ctl-start'
                      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'start' ? <LoadingSpinner /> : <PlayCircleIcon className='size-4' aria-hidden='true' />}
                      Start
                    </button>
                    <button
                      type='button'
                      onClick={() => { void handleGiveawayStop() }}
                      disabled={controlsBusy !== null || selectedGiveaway.status !== 'OPEN'}
                      data-testid='giveaway-ctl-stop'
                      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-400 px-3 py-2 text-xs font-semibold text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'stop' ? <LoadingSpinner /> : <StopCircleIcon className='size-4' aria-hidden='true' />}
                      Stop
                    </button>
                    <button
                      type='button'
                      onClick={() => { void handleGiveawayCalculate() }}
                      disabled={controlsBusy !== null || selectedGiveaway.status !== 'CLOSED' || selectedGiveaway.hasDraw}
                      data-testid='giveaway-ctl-calculate'
                      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-sky-500 px-3 py-2 text-xs font-semibold text-black hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'calculate' ? <LoadingSpinner /> : <SignalIcon className='size-4' aria-hidden='true' />}
                      Calculate winners
                    </button>
                    <button
                      type='button'
                      onClick={() => { void handleGiveawayPublish() }}
                      disabled={controlsBusy !== null || !selectedGiveaway.hasDraw || selectedGiveaway.status === 'ENDED'}
                      data-testid='giveaway-ctl-publish'
                      className='inline-flex items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'publish' ? <LoadingSpinner /> : <CheckIcon className='size-4' aria-hidden='true' />}
                      Publish winners
                    </button>
                    <button
                      type='button'
                      onClick={() => { void handleGiveawayReset() }}
                      disabled={controlsBusy !== null || isEnded}
                      data-testid='giveaway-ctl-reset'
                      className='col-span-2 inline-flex items-center justify-center gap-1.5 rounded-md bg-red-500 px-3 py-2 text-xs font-semibold text-black hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {controlsBusy === 'reset' ? <LoadingSpinner /> : <ArrowPathIcon className='size-4' aria-hidden='true' />}
                      Reset (testing only)
                    </button>
                  </div>
                  {controlsBusy === 'calculate' && (
                    <p className='text-[11px] text-sky-300' data-testid='giveaway-calculating-hint'>
                      Committing the entrant pool and waiting for the next drand round (~10 s)…
                    </p>
                  )}
                  {selectedGiveaway.hasDraw && selectedGiveaway.draw != null && (
                    <div data-testid='giveaway-panel-winners'>
                      <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
                        Drawn winners &amp; alternates
                      </div>
                      <GiveawayWinnersTable
                        draw={selectedGiveaway.draw}
                        canDisqualify={selectedGiveaway.status !== 'ENDED'}
                        busyId={typeof controlsBusy === 'string' && controlsBusy.startsWith('disqualify:') ? controlsBusy.slice('disqualify:'.length) : null}
                        disabled={controlsBusy !== null}
                        onDisqualify={(w) => { void handleGiveawayDisqualify(w) }}
                      />
                    </div>
                  )}
                  <p className='text-[11px] leading-relaxed text-dm-text-accent'>
                    Calculate freezes the entrants and draws from a public drand round; Publish flips the
                    overlay to the winners card. Disqualify and Export CSV live on the giveaway&apos;s form.
                  </p>
                </div>
                )
              : <p className='text-xs text-dm-text-accent'>This giveaway no longer exists.</p>
          )}
          {ytWarn != null && (
            <YouTubeChatMissingWarning
              key={ytWarn.seq}
              eventId={event.eventId}
              visible={ytWarnVisible}
              onDismiss={() => setYtWarn(null)}
            />
          )}
          {controlsError !== null && (
            <div className='mt-2'>
              <ErrorInline onDismiss={() => setControlsError(null)}>{controlsError}</ErrorInline>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

const OverlayEditorPageWithPreviewHub = ({ kind }: { kind: CompositeOverlayKind }): JSX.Element => {
  const { event } = useEventDetail()
  const { k } = useDefaultOverlayLink(event.eventId)
  const streamKey = useMemo(() => ({ eventId: event.eventId, k }), [event.eventId, k])
  return (
    <EditorPreviewHubProvider streamKey={streamKey}>
      <OverlayEditorPage kind={kind} overlayLinkK={k} />
    </EditorPreviewHubProvider>
  )
}

export { OverlayEditorPageWithPreviewHub as OverlayEditorPage }
